import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Modal from "../../../../ui/Modal";
import { fetchWithAuth } from "../../../../../utils/api";
import { swalConfirm, swalSuccess } from "../../../../../utils/swalAlert";
import { PDFDocument, degrees } from "pdf-lib";

// Dynamic PDF.js loader helper to guarantee PDF.js & worker availability everywhere
let pdfJsPromise = null;
const ensurePdfJsLoaded = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("Window not available"));

  if (window.pdfjsLib) {
    if (window.pdfjsLib.GlobalWorkerOptions && !window.pdfjsLib.GlobalWorkerOptions.workerSrc) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
    }
    return Promise.resolve(window.pdfjsLib);
  }

  if (pdfJsPromise) return pdfJsPromise;

  pdfJsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="pdf.min.js"]');
    const onReady = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        resolve(window.pdfjsLib);
      } else {
        reject(new Error("window.pdfjsLib not defined"));
      }
    };

    if (existing) {
      if (window.pdfjsLib) {
        onReady();
      } else {
        existing.addEventListener("load", onReady);
        existing.addEventListener("error", () => reject(new Error("Failed to load PDF.js")));
        setTimeout(onReady, 2500);
      }
      return;
    }

    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.async = true;
    script.onload = onReady;
    script.onerror = () => reject(new Error("Failed to load PDF.js script"));
    document.head.appendChild(script);
  });

  return pdfJsPromise;
};

/**
 * SmartSplitPageCanvas
 * Renders an individual page on-demand via single-page slice streaming or master PDF doc.
 * Features smart viewport lazy loading (IntersectionObserver with 300px pre-buffering),
 * immediate priority for full-view mode, sticky caching, and zero-CLS skeleton placeholders.
 */
const SmartSplitPageCanvas = ({
  testId,
  pageNumber,
  isThumbnail = false,
  rotation = 0,
  pdfDoc = null,
  pdfLoadError = false,
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [rendered, setRendered] = useState(false);
  const [renderError, setRenderError] = useState(false);
  // Full view renders immediately (Priority 1); thumbnails start false and lazy-load on viewport entry
  const [isVisible, setIsVisible] = useState(!isThumbnail);

  // Viewport Observer for Thumbnails: strictly respects the scrollable parent container viewport
  useEffect(() => {
    if (!isThumbnail) {
      setIsVisible(true);
      return;
    }

    // Sticky Caching: once marked visible, it stays loaded in DOM
    if (isVisible) return;

    const el = containerRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    // Strictly bind to the scrollable container (.smart-split-papers-list)
    const scrollRoot = el.closest(".smart-split-papers-list") || null;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      {
        root: scrollRoot,
        // Smooth 80px vertical pre-buffer: strictly loads ONLY cards that are in view or about to enter
        rootMargin: "80px 0px",
        threshold: 0.01,
      },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [isThumbnail, isVisible]);

  // PDF.js Canvas Rendering Effect: executes only when page is visible in/near viewport and pdfDoc is loaded
  useEffect(() => {
    if (!isVisible || !pageNumber || !pdfDoc || !canvasRef.current) {
      return;
    }

    let isCancelled = false;
    let renderTask = null;

    const render = async () => {
      try {
        setRendered(false);
        setRenderError(false);

        const page = await pdfDoc.getPage(pageNumber);
        if (isCancelled || !page) return;

        // Retrieve true intrinsic rotation from PDF.js
        const defaultViewport = page.getViewport({ scale: 1 });
        const targetRotation = (typeof rotation === "number" ? rotation : (defaultViewport.rotation || 0)) % 360;

        const scale = isThumbnail ? 0.35 : 2.0;
        const viewport = page.getViewport({ scale, rotation: targetRotation });

        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext("2d", { alpha: false });
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        renderTask = page.render({ canvasContext: ctx, viewport });
        await renderTask.promise;
        if (!isCancelled) {
          setRendered(true);
        }
      } catch (err) {
        if (!isCancelled && err?.name !== "RenderingCancelledException") {
          console.error(
            `[SmartSplitViewer] Error rendering page ${pageNumber}:`,
            err,
          );
          setRenderError(true);
        }
      }
    };

    render();

    return () => {
      isCancelled = true;
      if (renderTask) {
        try {
          renderTask.cancel();
        } catch (_) {}
      }
    };
  }, [isVisible, pdfDoc, pageNumber, isThumbnail, rotation]);

  // Zero-CLS Skeleton: Shown while off-screen or pending intersection
  if (!isVisible) {
    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: isThumbnail ? "100%" : "600px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#f1f5f9",
          color: "#94a3b8",
          borderRadius: "4px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)",
            animation: "spShimmer 1.6s ease-in-out infinite",
          }}
        />
        <i
          className="fa-regular fa-file"
          style={{
            fontSize: isThumbnail ? "0.9rem" : "2rem",
            color: "#cbd5e1",
            marginBottom: "2px",
          }}
        />
        <span
          style={{
            fontSize: isThumbnail ? "0.55rem" : "0.85rem",
            fontWeight: 600,
            color: "#94a3b8",
          }}
        >
          Pg #{pageNumber}
        </span>
      </div>
    );
  }

  if (pdfLoadError || renderError) {
    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: isThumbnail ? "100%" : "300px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fafc",
          color: "#94a3b8",
          fontSize: isThumbnail ? "0.55rem" : "0.85rem",
          gap: "6px",
        }}
      >
        <i className="fa-regular fa-file-pdf"></i>
        <span>Pg #{pageNumber}</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: isThumbnail ? "100%" : "auto",
        maxWidth: "100%",
        background: "#ffffff",
      }}
    >
      <canvas
        ref={canvasRef}
        style={
          isThumbnail
            ? {
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }
            : {
                width: "100%",
                height: "auto",
                display: "block",
                margin: "0 auto",
                borderRadius: "4px",
              }
        }
      />
      {!rendered && (
        <div
          style={{
            position: isThumbnail ? "absolute" : "relative",
            inset: 0,
            height: isThumbnail ? "100%" : "600px",
            minHeight: isThumbnail ? "auto" : "400px",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f8fafc",
            color: "#94a3b8",
            fontSize: isThumbnail ? "0.55rem" : "0.85rem",
          }}
        >
          <i
            className="fa-solid fa-circle-notch fa-spin"
            style={{
              color: "#3b82f6",
              fontSize: isThumbnail ? "0.9rem" : "1.8rem",
            }}
          ></i>
        </div>
      )}
    </div>
  );
};

// Paper Status Helpers
export const isPaperAutoGraded = (paper) => {
  if (!paper) return false;
  return Boolean(paper.isSubmitted || paper.answerPaperId);
};

// An Answer Paper is Ready if and only if it has pages, an assigned student, has no unresolved anomalies or conflicts, and is NOT yet submitted/graded
export const isPaperReady = (paper) => {
  if (!paper) return false;
  if (!paper.pageNumbers || paper.pageNumbers.length === 0) return false;
  if (isPaperAutoGraded(paper)) return false;
  if (paper.matchStatus === 'conflict') return false;
  if (paper.pageAnomaly?.hasAnomaly && !paper.isAnomalyDismissed) return false;
  return Boolean(paper.mappedStudentUserId && paper.mappedStudentName);
};

export const isPaperReview = (paper) => {
  if (!paper) return false;
  if (isPaperAutoGraded(paper)) return false;
  return !isPaperReady(paper);
};

const SmartSplitDocumentViewer = ({
  isOpen,
  onClose,
  smartSplitResult,
  testId,
  onPaperUpdated,
  onSave,
  onSubmissionsUpdated,
  onPageRotated,
}) => {
  const activeTestId = testId || smartSplitResult?.testId || smartSplitResult?._id;

  // Navigation & focus state
  const [activePaper, setActivePaper] = useState(() => {
    const rawPapers = smartSplitResult?.papers ?? [];
    const firstRev = rawPapers.findIndex(
      (p) =>
        !p.isSubmitted &&
        !p.answerPaperId &&
        (!p.mappedStudentUserId || !p.mappedStudentName || (p.pageAnomaly?.hasAnomaly && !p.isAnomalyDismissed)),
    );
    return firstRev !== -1 ? firstRev : 0;
  });
  const [activePageNumber, setActivePageNumber] = useState(null);
  const [zoom, setZoom] = useState(100); // 40% - 200%
  const [mobileTab, setMobileTab] = useState("papers"); // 'papers' | 'preview'

  // Filter & Search states for Vertical List
  const [filterTab, setFilterTab] = useState("all"); // 'all' | 'ready' | 'review'
  const [paperSearchQuery, setPaperSearchQuery] = useState("");

  // Dynamic document data & history
  const [papers, setPapers] = useState(() => {
    const rawPapers = smartSplitResult?.papers ?? [];
    const currentEnrollments = smartSplitResult?.enrollments ?? [];
    return rawPapers.map((paper) => {
      let mappedRollNo = paper.mappedStudentRollNo || paper.mappedRollNo;
      if (
        !mappedRollNo &&
        (paper.mappedStudentUserId || paper.mappedStudentName)
      ) {
        const matched = currentEnrollments.find(
          (e) =>
            (paper.mappedStudentUserId &&
              String(e.userId || e._id) ===
                String(paper.mappedStudentUserId)) ||
            (paper.mappedStudentName &&
              e.name &&
              e.name.trim().toLowerCase() ===
                paper.mappedStudentName.trim().toLowerCase()),
        );
        if (matched?.rollNo || matched?.studentId) {
          mappedRollNo = String(matched.rollNo || matched.studentId).trim();
        }
      }
      return {
        ...paper,
        mappedStudentRollNo: mappedRollNo || null,
        mappedRollNo: mappedRollNo || null,
      };
    });
  });
  const [pageRotations, setPageRotations] = useState(() => {
    const initial = {};
    if (Array.isArray(smartSplitResult?.pages)) {
      smartSplitResult.pages.forEach((p) => {
        if (p.pageNumber && typeof p.rotation === "number") {
          initial[p.pageNumber] = p.rotation;
        }
      });
    }
    return initial;
  });

  // Synchronize page rotations whenever smartSplitResult updates without needing a full page reload
  useEffect(() => {
    if (Array.isArray(smartSplitResult?.pages) && smartSplitResult.pages.length > 0) {
      setPageRotations((prev) => {
        let changed = false;
        const next = { ...prev };
        smartSplitResult.pages.forEach((p) => {
          if (p.pageNumber && typeof p.rotation === "number" && next[p.pageNumber] !== p.rotation) {
            next[p.pageNumber] = p.rotation;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [smartSplitResult?.pages]);
  const [showAbsentDrawer, setShowAbsentDrawer] = useState(false);
  const [history, setHistory] = useState([]);
  const [undoToast, setUndoToast] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showCloseGuard, setShowCloseGuard] = useState(false);

  // Student remapping dropdown state
  const [remappingTargetPaperIdx, setRemappingTargetPaperIdx] = useState(null);
  const [dropdownCoords, setDropdownCoords] = useState(null); // { left, top, bottom, placement }
  const [studentSearchQuery, setStudentSearchQuery] = useState("");
  const remappingRef = useRef(null);

  // Modal states for user-friendly flows
  const [overrideConfirmState, setOverrideConfirmState] = useState(null); // { student, targetPaperIdx, existingPaperIdx }
  const [unassignConfirmState, setUnassignConfirmState] = useState(null); // { paperIdx, studentName }
  const [movePageModalState, setMovePageModalState] = useState(null); // { pageNumber, sourcePaperIdx }

  // Merge dropdown state (Floating popover matching Assignment/Change UI)
  const [mergeTargetPaperIdx, setMergeTargetPaperIdx] = useState(null);
  const [mergeDropdownCoords, setMergeDropdownCoords] = useState(null);
  const [mergeSearchQuery, setMergeSearchQuery] = useState("");
  const mergeDropdownRef = useRef(null);

  // Drag & drop state for reordering & moving
  const [draggedPage, setDraggedPage] = useState(null); // { sourcePaperIdx, pageNumber }
  const [dragOverTarget, setDragOverTarget] = useState(null); // { targetPaperIdx, targetPageIndex }

  // Batch Client-Side Slicing & Upload States
  const [batchModalState, setBatchModalState] = useState(null);
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const masterPdfBufferRef = useRef(null);
  const uploadAbortControllerRef = useRef(null);

  // Master PDF ArrayBuffer cache - memoized and reusable for both preview rendering & student slicing
  const getMasterPdfBuffer = useCallback(async () => {
    if (masterPdfBufferRef.current) return masterPdfBufferRef.current;
    if (!smartSplitResult?.masterPdfUrl) {
      throw new Error("Master scan PDF URL is not available.");
    }
    const res = await fetch(smartSplitResult.masterPdfUrl);
    if (!res.ok) {
      throw new Error("Could not download master scan PDF.");
    }
    const buffer = await res.arrayBuffer();
    masterPdfBufferRef.current = buffer;
    return buffer;
  }, [smartSplitResult?.masterPdfUrl]);

  const isDirty = history.length > 0;
  const canvasViewportRef = useRef(null);
  const prevIsOpenRef = useRef(false);

  // Direct Master PDF.js Document instance loaded from GCS signed URL (zero backend server CPU/RAM)
  const [masterPdfJsDoc, setMasterPdfJsDoc] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(false);

  useEffect(() => {
    if (!isOpen || !smartSplitResult?.masterPdfUrl) {
      setMasterPdfJsDoc((prevDoc) => {
        if (prevDoc) {
          try { prevDoc.destroy(); } catch (_) {}
        }
        return null;
      });
      return;
    }

    let isCancelled = false;
    let loadingTask = null;

    const loadMasterPdfDirectly = async () => {
      try {
        setPdfLoadError(false);
        const pdfjs = await ensurePdfJsLoaded();
        loadingTask = pdfjs.getDocument({
          url: smartSplitResult.masterPdfUrl,
          rangeChunkSize: 262144, // 256KB chunk range requests (optimizes HTTP round-trips by 4x)
          disableAutoFetch: true, // Strictly fetch chunks ONLY for pages actually viewed
          disableStream: true, // CRITICAL: Stop progressive background stream downloading of the full file
          cMapUrl: "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/",
          cMapPacked: true,
        });

        const doc = await loadingTask.promise;
        if (!isCancelled) {
          setMasterPdfJsDoc((prevDoc) => {
            if (prevDoc) {
              try { prevDoc.destroy(); } catch (_) {}
            }
            return doc;
          });
        }
      } catch (err) {
        if (!isCancelled) {
          console.warn("[SmartSplitViewer] Direct master PDF load warning, will fallback to backend slice:", err);
          setPdfLoadError(true);
        }
      }
    };

    loadMasterPdfDirectly();

    return () => {
      isCancelled = true;
      if (loadingTask) {
        try {
          loadingTask.destroy();
        } catch (_) {}
      }
      setMasterPdfJsDoc((prevDoc) => {
        if (prevDoc) {
          try {
            prevDoc.destroy();
          } catch (_) {}
        }
        return null;
      });
    };
  }, [isOpen, smartSplitResult?.masterPdfUrl]);

  // Clear any loaded buffer when modal closes
  useEffect(() => {
    if (!isOpen) {
      masterPdfBufferRef.current = null;
    }
  }, [isOpen]);

  // Reset and populate viewer ONLY when modal transitions from closed to open
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = Boolean(isOpen);

    if (isOpen && !wasOpen) {
      const rawPapers = smartSplitResult?.papers ?? [];
      const currentEnrollments = smartSplitResult?.enrollments ?? [];
      const initialPapers = rawPapers.map((paper) => {
        let mappedRollNo = paper.mappedStudentRollNo || paper.mappedRollNo;
        if (
          !mappedRollNo &&
          (paper.mappedStudentUserId || paper.mappedStudentName)
        ) {
          const matched = currentEnrollments.find(
            (e) =>
              (paper.mappedStudentUserId &&
                String(e.userId || e._id) ===
                  String(paper.mappedStudentUserId)) ||
              (paper.mappedStudentName &&
                e.name &&
                e.name.trim().toLowerCase() ===
                  paper.mappedStudentName.trim().toLowerCase()),
          );
          if (matched?.rollNo || matched?.studentId) {
            mappedRollNo = String(matched.rollNo || matched.studentId).trim();
          }
        }
        return {
          ...paper,
          mappedStudentRollNo: mappedRollNo || null,
          mappedRollNo: mappedRollNo || null,
        };
      });
      setPapers(initialPapers);
      setActivePaper(0);
      if (initialPapers[0]?.pageNumbers?.[0]) {
        setActivePageNumber(initialPapers[0].pageNumbers[0]);
      } else {
        setActivePageNumber(1);
      }
      const initialRotations = {};
      if (Array.isArray(smartSplitResult?.pages)) {
        smartSplitResult.pages.forEach((p) => {
          if (p.pageNumber && p.rotation) {
            initialRotations[p.pageNumber] = p.rotation;
          }
        });
      }
      setPageRotations(initialRotations);
      setShowAbsentDrawer(false);
      setFilterTab("all");
      setPaperSearchQuery("");
      setHistory([]);
      setUndoToast(null);
      setBatchModalState(null);
      setIsBatchUploading(false);
      setRemappingTargetPaperIdx(null);
      setOverrideConfirmState(null);
      setUnassignConfirmState(null);
      setMovePageModalState(null);
      setMergeTargetPaperIdx(null);
      setMergeDropdownCoords(null);
      setMergeSearchQuery("");
    }
  }, [isOpen, smartSplitResult]);

  const enrollments = useMemo(
    () => smartSplitResult?.enrollments ?? [],
    [smartSplitResult],
  );

  // Helper to resolve student roll number from paper or enrollments (only actual mapped student roll no)
  const getPaperRollNo = useCallback(
    (paper) => {
      if (!paper) return null;
      if (paper.mappedStudentRollNo)
        return String(paper.mappedStudentRollNo).trim();
      if (paper.mappedRollNo) return String(paper.mappedRollNo).trim();
      if (enrollments && enrollments.length > 0) {
        const student = enrollments.find(
          (e) =>
            (paper.mappedStudentUserId &&
              String(e.userId || e._id) ===
                String(paper.mappedStudentUserId)) ||
            (paper.mappedStudentName &&
              e.name &&
              e.name.trim().toLowerCase() ===
                paper.mappedStudentName.trim().toLowerCase()),
        );
        const roll = student?.rollNo || student?.studentId;
        if (roll) return String(roll).trim();
      }
      return null;
    },
    [enrollments],
  );

  const currentPaper = papers[activePaper] || null;
  const currentPaperPages = useMemo(() => {
    return currentPaper?.pageNumbers || [];
  }, [currentPaper]);

  const activePageIdxInPaper = useMemo(() => {
    if (!currentPaperPages.length) return 0;
    const idx = currentPaperPages.indexOf(activePageNumber);
    return idx >= 0 ? idx : 0;
  }, [currentPaperPages, activePageNumber]);

  const displayBulkPn =
    currentPaperPages[activePageIdxInPaper] || activePageNumber;

  // Ensure activePageNumber stays valid if paper changes
  useEffect(() => {
    if (
      currentPaperPages.length > 0 &&
      !currentPaperPages.includes(activePageNumber)
    ) {
      setActivePageNumber(currentPaperPages[0]);
    }
  }, [currentPaperPages, activePageNumber]);

  // Close student remapping dropdown on outside click or scroll
  useEffect(() => {
    if (remappingTargetPaperIdx === null) return;
    const handleOutsideClick = (e) => {
      if (remappingRef.current && !remappingRef.current.contains(e.target)) {
        setRemappingTargetPaperIdx(null);
        setDropdownCoords(null);
      }
    };
    const handleScroll = (e) => {
      if (remappingRef.current && !remappingRef.current.contains(e.target)) {
        setRemappingTargetPaperIdx(null);
        setDropdownCoords(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [remappingTargetPaperIdx]);

  // Close merge dropdown on outside click or scroll
  useEffect(() => {
    if (mergeTargetPaperIdx === null) return;
    const handleOutsideClick = (e) => {
      if (
        mergeDropdownRef.current &&
        !mergeDropdownRef.current.contains(e.target)
      ) {
        setMergeTargetPaperIdx(null);
        setMergeDropdownCoords(null);
      }
    };
    const handleScroll = (e) => {
      if (
        mergeDropdownRef.current &&
        !mergeDropdownRef.current.contains(e.target)
      ) {
        setMergeTargetPaperIdx(null);
        setMergeDropdownCoords(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [mergeTargetPaperIdx]);

  // Candidate papers for merge: ONLY papers with actual student data (excluding source paper)
  const mergeCandidatePapers = useMemo(() => {
    if (mergeTargetPaperIdx === null) return [];
    return papers
      .map((paper, pIdx) => {
        const rollNo = getPaperRollNo(paper);
        return { paper, pIdx, rollNo };
      })
      .filter(({ paper, pIdx }) => {
        if (pIdx === mergeTargetPaperIdx) return false;
        // Merge can ONLY happen with actual student data!
        return Boolean(paper.mappedStudentName);
      })
      .filter(({ paper, pIdx, rollNo }) => {
        if (!mergeSearchQuery.trim()) return true;
        const q = mergeSearchQuery.toLowerCase().trim();
        const name = (paper.mappedStudentName || "").toLowerCase();
        const roll = String(rollNo || "").toLowerCase();
        const paperNum = `answer paper #${pIdx + 1}`.toLowerCase();
        return name.includes(q) || roll.includes(q) || paperNum.includes(q);
      });
  }, [papers, mergeTargetPaperIdx, mergeSearchQuery, getPaperRollNo]);

  // Push history snapshot before mutation
  const pushHistorySnapshot = useCallback(() => {
    setHistory((prev) => [
      ...prev,
      { papers: JSON.parse(JSON.stringify(papers)), pageRotations },
    ]);
  }, [papers, pageRotations]);

  // Undo last action
  const undoLastAction = useCallback(() => {
    if (history.length === 0) return;
    const previousSnapshot = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setPapers(previousSnapshot.papers);
    setPageRotations(previousSnapshot.pageRotations || {});
    onPaperUpdated?.(previousSnapshot.papers);
    setUndoToast(null);

    const safePaperIdx = Math.min(
      activePaper,
      Math.max(0, previousSnapshot.papers.length - 1),
    );
    setActivePaper(safePaperIdx);
    if (previousSnapshot.papers[safePaperIdx]?.pageNumbers?.[0]) {
      setActivePageNumber(previousSnapshot.papers[safePaperIdx].pageNumbers[0]);
    }
  }, [history, activePaper, onPaperUpdated]);

  // Keyboard navigation & shortcuts
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName)) return;

      // Undo: Ctrl+Z or Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undoLastAction();
        return;
      }

      // Next / Previous paper: ArrowLeft / ArrowRight
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigateToAdjacentPaper(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        navigateToAdjacentPaper(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  // Navigate to next or previous paper
  const navigateToAdjacentPaper = (direction) => {
    if (papers.length === 0) return;

    let targetIdx = activePaper + direction;
    if (targetIdx < 0) targetIdx = 0;
    if (targetIdx >= papers.length) targetIdx = papers.length - 1;

    if (targetIdx !== activePaper) {
      setActivePaper(targetIdx);
      if (papers[targetIdx]?.pageNumbers?.[0]) {
        setActivePageNumber(papers[targetIdx].pageNumbers[0]);
      }
      if (canvasViewportRef.current) {
        canvasViewportRef.current.scrollTop = 0;
      }
      // Scroll the selected paper card into view in the sidebar
      const cardEl = document.getElementById(`smart-split-card-${targetIdx}`);
      if (cardEl) {
        cardEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  };

  // Reorder or Move page via Drag and Drop (handles both intra-paper and inter-paper)
  const handlePageDrop = (
    sourcePaperIdx,
    sourcePageNumber,
    targetPaperIdx,
    targetInsertIndex,
  ) => {
    if (sourcePaperIdx < 0 || sourcePaperIdx >= papers.length) return;
    if (targetPaperIdx < 0 || targetPaperIdx >= papers.length) return;

    // 1. Intra-paper reordering
    if (sourcePaperIdx === targetPaperIdx) {
      const currentPages = papers[sourcePaperIdx].pageNumbers || [];
      const fromIndex = currentPages.indexOf(sourcePageNumber);
      if (fromIndex === -1) return;

      let toIndex = targetInsertIndex;
      if (fromIndex < toIndex) {
        toIndex = toIndex - 1;
      }
      toIndex = Math.max(0, Math.min(currentPages.length - 1, toIndex));
      if (fromIndex === toIndex) return; // Dropped in same position

      pushHistorySnapshot();

      const nextPapers = papers.map((p, idx) => {
        if (idx !== sourcePaperIdx) return p;
        const newPages = [...p.pageNumbers];
        const [moved] = newPages.splice(fromIndex, 1);
        newPages.splice(toIndex, 0, moved);
        return { ...p, pageNumbers: newPages };
      });

      setPapers(nextPapers);
      setActivePaper(sourcePaperIdx);
      setActivePageNumber(sourcePageNumber);
      onPaperUpdated?.(nextPapers);

      setUndoToast({
        message: `Page #${sourcePageNumber} reordered in Answer Paper #${sourcePaperIdx + 1}.`,
        timestamp: Date.now(),
      });
      return;
    }

    // 2. Inter-paper move
    pushHistorySnapshot();

    const nextPapers = papers.map((p) => ({
      ...p,
      pageNumbers: [...(p.pageNumbers || [])],
    }));

    // Remove from source paper
    nextPapers[sourcePaperIdx].pageNumbers = nextPapers[
      sourcePaperIdx
    ].pageNumbers.filter((pn) => pn !== sourcePageNumber);

    // Insert into target paper at desired index
    const targetPages = nextPapers[targetPaperIdx].pageNumbers;
    const clampedIndex = Math.max(
      0,
      Math.min(targetPages.length, targetInsertIndex),
    );
    targetPages.splice(clampedIndex, 0, sourcePageNumber);

    // Auto-prune source paper if it has 0 pages left
    let prunedPapers = nextPapers.filter((p) => p.pageNumbers.length > 0);

    let nextActivePaper = targetPaperIdx;
    if (nextActivePaper >= prunedPapers.length) {
      nextActivePaper = Math.max(0, prunedPapers.length - 1);
    }

    setPapers(prunedPapers);
    setActivePaper(nextActivePaper);
    setActivePageNumber(sourcePageNumber);
    onPaperUpdated?.(prunedPapers);

    const targetName =
      prunedPapers[nextActivePaper]?.mappedStudentName ||
      prunedPapers[nextActivePaper]?.extractedStudentName ||
      `Answer Paper #${nextActivePaper + 1}`;

    setUndoToast({
      message: `Page #${sourcePageNumber} moved to ${targetName}.`,
      timestamp: Date.now(),
    });
  };

  // Move Page to Another Answer Paper (UX-Friendly alternative to Drag & Drop)
  const handleMovePageToPaper = (
    sourcePaperIdx,
    pageNumber,
    targetPaperIdx,
  ) => {
    if (sourcePaperIdx < 0 || sourcePaperIdx >= papers.length) return;
    if (
      isPaperAutoGraded(papers[sourcePaperIdx]) ||
      (targetPaperIdx !== "new" && isPaperAutoGraded(papers[targetPaperIdx]))
    ) {
      setUndoToast({
        message: "Cannot move pages to or from an auto-graded paper.",
        timestamp: Date.now(),
      });
      return;
    }

    pushHistorySnapshot();

    let nextPapers = papers.map((p) => ({
      ...p,
      pageNumbers: [...(p.pageNumbers || [])],
    }));

    // Remove page from source
    nextPapers[sourcePaperIdx].pageNumbers = nextPapers[
      sourcePaperIdx
    ].pageNumbers.filter((pn) => pn !== pageNumber);

    let destinationIdx = targetPaperIdx;

    if (targetPaperIdx === "new") {
      // Create a brand new Answer Paper containing this page
      const newPaper = {
        paperIndex: nextPapers.length,
        pageNumbers: [pageNumber],
        mappedStudentUserId: null,
        mappedStudentName: null,
        mappedStudentRollNo: null,
        mappedRollNo: null,
        extractedStudentName: null,
        extractedRollNo: null,
        matchStatus: "unconfirmed_roll",
        confidenceScore: 0,
        statusMessage: `New Answer Paper created with page #${pageNumber}.`,
        isApproved: false,
      };
      nextPapers.push(newPaper);
      destinationIdx = nextPapers.length - 1;
    } else if (targetPaperIdx >= 0 && targetPaperIdx < nextPapers.length) {
      nextPapers[targetPaperIdx].pageNumbers.push(pageNumber);
    }

    // Auto-prune source paper if it has 0 pages left
    const sourceEmpty = nextPapers[sourcePaperIdx].pageNumbers.length === 0;
    let prunedPapers = nextPapers.filter((p) => p.pageNumbers.length > 0);

    if (sourceEmpty && destinationIdx > sourcePaperIdx) {
      destinationIdx -= 1;
    }
    destinationIdx = Math.max(
      0,
      Math.min(prunedPapers.length - 1, destinationIdx),
    );

    setPapers(prunedPapers);
    setActivePaper(destinationIdx);
    setActivePageNumber(pageNumber);
    setMovePageModalState(null);
    onPaperUpdated?.(prunedPapers);

    const targetName =
      prunedPapers[destinationIdx]?.mappedStudentName ||
      `Answer Paper #${destinationIdx + 1}`;

    setUndoToast({
      message: `Page #${pageNumber} moved to ${targetName}.`,
      timestamp: Date.now(),
    });
  };

  // Split Answer Paper right between two pages (Dangerous operation -> requires swalConfirm)
  const handleSplitPaperAtPage = async (paperIdx, pageNumber) => {
    if (paperIdx < 0 || paperIdx >= papers.length) return;
    const paper = papers[paperIdx];
    if (isPaperAutoGraded(paper)) {
      setUndoToast({
        message: "Cannot split an already auto-graded paper.",
        timestamp: Date.now(),
      });
      return;
    }
    const pageIndexInPaper = paper.pageNumbers.indexOf(pageNumber);
    if (pageIndexInPaper <= 0) return; // Cannot split before the first page

    const firstSegmentPages = paper.pageNumbers.slice(0, pageIndexInPaper);
    const secondSegmentPages = paper.pageNumbers.slice(pageIndexInPaper);

    const startPage = secondSegmentPages[0];
    const endPage = secondSegmentPages[secondSegmentPages.length - 1];

    const pageRangeText =
      startPage === endPage
        ? `Page #${startPage}`
        : `Pages from ${startPage} to ${endPage}`;

    const studentDesc = paper.mappedStudentName
      ? `(${paper.mappedStudentName})`
      : "(Unassigned)";

    const confirmResult = await swalConfirm(
      "Split Answer Paper?",
      `Are you sure you want to split Answer Paper of ${studentDesc}? ${pageRangeText} will be separated into a new unassigned Answer Paper.`,
      "Yes, Split Paper",
      "Cancel",
    );

    if (!confirmResult || !confirmResult.isConfirmed) {
      return;
    }

    pushHistorySnapshot();

    const updatedPaper = {
      ...paper,
      pageNumbers: firstSegmentPages,
    };

    const newPaperIndex = papers.length;
    const splitToastRangeText =
      startPage === endPage
        ? `Page #${startPage}`
        : `Pages from pg #${startPage} to page #${endPage}`;

    const splitMessage = `${splitToastRangeText} is now separated from Answer Paper #${paperIdx + 1} and moved to new Answer Paper #${paperIdx + 2} (Unassigned).`;

    const newPaper = {
      paperIndex: newPaperIndex,
      pageNumbers: secondSegmentPages,
      mappedStudentUserId: null,
      mappedStudentName: null,
      mappedStudentRollNo: null,
      mappedRollNo: null,
      extractedStudentName: null,
      extractedRollNo: null,
      matchStatus: "unconfirmed_roll",
      confidenceScore: 0,
      statusMessage: splitMessage,
      isApproved: false,
    };

    const nextPapers = [...papers];
    nextPapers.splice(paperIdx, 1, updatedPaper, newPaper);

    setPapers(nextPapers);
    setActivePaper(paperIdx + 1); // Focus the newly created answer paper
    setActivePageNumber(pageNumber);
    onPaperUpdated?.(nextPapers);

    setUndoToast({
      message: splitMessage,
      timestamp: Date.now(),
    });
  };

  // Dismiss page anomaly warning for an answer paper (e.g. teacher confirms student legitimately has extra pages)
  const handleDismissAnomaly = (paperIdx) => {
    if (paperIdx < 0 || paperIdx >= papers.length) return;
    pushHistorySnapshot();

    const nextPapers = papers.map((p, idx) => {
      if (idx !== paperIdx) return p;
      return {
        ...p,
        isAnomalyDismissed: true,
      };
    });

    setPapers(nextPapers);
    onPaperUpdated?.(nextPapers);

    setUndoToast({
      message: `Page count warning dismissed for Answer Paper #${paperIdx + 1}.`,
      timestamp: Date.now(),
    });
  };

  // Merge Answer Paper with another target Answer Paper
  const handleMergePapers = (sourcePaperIdx, targetPaperIdx) => {
    if (
      sourcePaperIdx < 0 ||
      sourcePaperIdx >= papers.length ||
      targetPaperIdx < 0 ||
      targetPaperIdx >= papers.length ||
      sourcePaperIdx === targetPaperIdx
    ) {
      return;
    }

    const targetPaper = papers[targetPaperIdx];
    const sourcePaper = papers[sourcePaperIdx];
    if (isPaperAutoGraded(sourcePaper) || isPaperAutoGraded(targetPaper)) {
      setUndoToast({
        message: "Cannot merge an already auto-graded paper.",
        timestamp: Date.now(),
      });
      return;
    }

    // Allowed if targetPaper has student data, OR if sourcePaper has student data, OR if merging with adjacent previous paper
    const isAdjacentPrev = targetPaperIdx === sourcePaperIdx - 1;
    if (
      !targetPaper?.mappedStudentName &&
      !sourcePaper?.mappedStudentName &&
      !isAdjacentPrev
    ) {
      return;
    }

    pushHistorySnapshot();

    // Combine page numbers and sort in ascending numerical order
    const mergedPages = Array.from(
      new Set([
        ...(targetPaper.pageNumbers || []),
        ...(sourcePaper.pageNumbers || []),
      ]),
    ).sort((a, b) => a - b);

    // Target has actual student data, so preserve target's student data (fallback to source student data)
    const studentUserId =
      targetPaper.mappedStudentUserId ||
      sourcePaper.mappedStudentUserId ||
      null;
    const studentName =
      targetPaper.mappedStudentName || sourcePaper.mappedStudentName || null;
    const rollNo =
      targetPaper.mappedStudentRollNo ||
      targetPaper.mappedRollNo ||
      getPaperRollNo(targetPaper) ||
      sourcePaper.mappedStudentRollNo ||
      sourcePaper.mappedRollNo ||
      getPaperRollNo(sourcePaper) ||
      null;
    const extractedName =
      targetPaper.extractedStudentName ||
      sourcePaper.extractedStudentName ||
      null;
    const extractedRoll =
      targetPaper.extractedRollNo || sourcePaper.extractedRollNo || null;

    const targetDesc = studentName
      ? `${studentName}'s Answer Paper`
      : `Answer Paper #${targetPaperIdx + 1}`;

    const mergedPaper = {
      ...targetPaper,
      pageNumbers: mergedPages,
      mappedStudentUserId: studentUserId,
      mappedStudentName: studentName,
      mappedStudentRollNo: rollNo,
      mappedRollNo: rollNo,
      extractedStudentName: extractedName,
      extractedRollNo: extractedRoll,
      statusMessage: `Merged Answer Paper #${sourcePaperIdx + 1} into ${targetDesc}.`,
    };

    // Update target paper and remove source paper
    let nextPapers = papers.map((p, idx) =>
      idx === targetPaperIdx ? mergedPaper : p,
    );
    nextPapers = nextPapers.filter((_, idx) => idx !== sourcePaperIdx);

    // Calculate new target index in the updated array
    let newTargetIdx = targetPaperIdx;
    if (sourcePaperIdx < targetPaperIdx) {
      newTargetIdx -= 1;
    }
    newTargetIdx = Math.max(0, Math.min(nextPapers.length - 1, newTargetIdx));

    setPapers(nextPapers);
    setActivePaper(newTargetIdx);
    if (mergedPages.length > 0) {
      setActivePageNumber(mergedPages[0]);
    }
    setMergeTargetPaperIdx(null);
    setMergeDropdownCoords(null);
    onPaperUpdated?.(nextPapers);

    setUndoToast({
      message: `Merged Answer Paper #${sourcePaperIdx + 1} into ${targetDesc} (${mergedPages.length} total pages).`,
      timestamp: Date.now(),
    });
  };

  // Rotate a page 90 degrees clockwise (Optimistic UI + Real Backend Persistence)
  const handleRotatePage = async (pageNumber) => {
    if (!pageNumber) return;
    const newAngle = ((pageRotations[pageNumber] || 0) + 90) % 360;

    // 1. Optimistic UI update immediately
    setPageRotations((prev) => ({
      ...prev,
      [pageNumber]: newAngle,
    }));

    // 2. Synchronously update smartSplitResult so re-renders / re-opens within session are never reset
    if (Array.isArray(smartSplitResult?.pages)) {
      const pageEntry = smartSplitResult.pages.find((p) => p.pageNumber === pageNumber);
      if (pageEntry) {
        pageEntry.rotation = newAngle;
      } else {
        smartSplitResult.pages.push({ pageNumber, rotation: newAngle, aiStatus: "done" });
      }
    }

    // 3. Notify parent component to update its stored status state
    onPageRotated?.(pageNumber, newAngle);

    // 4. Persist real rotation in DB (< 20ms)
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || "";
      const res = await fetchWithAuth(`${serverUrl}/api/smart-split/${activeTestId}/rotate-page`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pageNumber, angle: 90 }),
      });

      if (res && res.ok) {
        setUndoToast({
          message: `✓ Page #${pageNumber} rotated to ${newAngle}° and saved permanently.`,
          timestamp: Date.now(),
        });
      } else {
        console.warn("[SmartSplitViewer] Server rotation response was not ok:", res?.status);
      }
    } catch (err) {
      console.error("[SmartSplitViewer] Failed to save rotation to server:", err);
    }
  };

  // Unassign student from Answer Paper
  const handleUnassignStudent = (paperIdx) => {
    if (paperIdx < 0 || paperIdx >= papers.length) return;
    pushHistorySnapshot();

    const nextPapers = [...papers];
    const studentName = nextPapers[paperIdx].mappedStudentName || "Student";

    nextPapers[paperIdx] = {
      ...nextPapers[paperIdx],
      mappedStudentUserId: null,
      mappedStudentName: null,
      mappedStudentRollNo: null,
      mappedRollNo: null,
      matchStatus: "unmatched",
      confidenceScore: 0,
      isApproved: false,
      statusMessage: "Unassigned by teacher.",
    };

    setPapers(nextPapers);
    onPaperUpdated?.(nextPapers);

    setUndoToast({
      message: `Unassigned ${studentName} from Answer Paper #${paperIdx + 1}.`,
      timestamp: Date.now(),
    });
  };

  // Initiate Student Assignment (Checks for duplicate student assignment)
  const handleAssignStudentClick = (student, targetPaperIdx = activePaper) => {
    if (targetPaperIdx < 0 || targetPaperIdx >= papers.length) return;

    // Check if student is already mapped to a different Answer Paper
    const conflictPaperIdx = papers.findIndex((p, idx) => {
      if (idx === targetPaperIdx) return false;
      const matchId = student.userId || student._id;
      if (
        p.mappedStudentUserId &&
        matchId &&
        String(p.mappedStudentUserId) === String(matchId)
      ) {
        return true;
      }
      if (
        p.mappedStudentName &&
        student.name &&
        p.mappedStudentName.trim().toLowerCase() ===
          student.name.trim().toLowerCase()
      ) {
        return true;
      }
      return false;
    });

    if (conflictPaperIdx !== -1) {
      if (isPaperAutoGraded(papers[conflictPaperIdx])) {
        setUndoToast({
          message: `${student.name} is already auto-graded and submitted in Answer Paper #${conflictPaperIdx + 1}.`,
          timestamp: Date.now(),
        });
        return;
      }
      // Show Override confirmation popup
      setOverrideConfirmState({
        student,
        targetPaperIdx,
        existingPaperIdx: conflictPaperIdx,
      });
      return;
    }

    // Direct assignment
    applyStudentAssignment(student, targetPaperIdx);
  };

  // Execute assignment (with optional override clearing previous paper)
  const applyStudentAssignment = (
    student,
    targetPaperIdx,
    isOverriding = false,
    previousPaperIdx = null,
  ) => {
    pushHistorySnapshot();
    const nextPapers = [...papers];

    if (
      isOverriding &&
      previousPaperIdx !== null &&
      previousPaperIdx >= 0 &&
      previousPaperIdx < nextPapers.length
    ) {
      nextPapers[previousPaperIdx] = {
        ...nextPapers[previousPaperIdx],
        mappedStudentUserId: null,
        mappedStudentName: null,
        mappedStudentRollNo: null,
        mappedRollNo: null,
        matchStatus: "unconfirmed_roll",
        confidenceScore: 0,
        isApproved: false,
        statusMessage: `Unassigned because student was moved to Answer Paper #${targetPaperIdx + 1}.`,
      };
    }

    const assignedRoll = student.rollNo || student.studentId || null;
    nextPapers[targetPaperIdx] = {
      ...nextPapers[targetPaperIdx],
      mappedStudentUserId: student.userId || student._id || null,
      mappedStudentName: student.name || null,
      mappedStudentRollNo: assignedRoll,
      mappedRollNo: assignedRoll,
      matchStatus: "matched",
      confidenceScore: 1.0,
      isApproved: true,
      statusMessage: `Assigned to ${student.name || ""}.`,
    };

    setPapers(nextPapers);
    setRemappingTargetPaperIdx(null);
    setDropdownCoords(null);
    setOverrideConfirmState(null);
    onPaperUpdated?.(nextPapers);

    setUndoToast({
      message: isOverriding
        ? `Assigned to ${student.name}. Answer Paper #${previousPaperIdx + 1} is now unassigned.`
        : `Assigned to ${student.name}.`,
      timestamp: Date.now(),
    });
  };

  // Unsubmitted / Absent enrolled students calculation
  const unsubmittedStudents = useMemo(() => {
    if (!enrollments || enrollments.length === 0) return [];
    const mappedIds = new Set(
      papers
        .filter((p) => p.mappedStudentUserId)
        .map((p) => String(p.mappedStudentUserId)),
    );
    const mappedNames = new Set(
      papers
        .filter((p) => p.mappedStudentName)
        .map((p) => p.mappedStudentName.toLowerCase()),
    );

    return enrollments.filter((s) => {
      const hasId = s.userId && mappedIds.has(String(s.userId));
      const hasName = s.name && mappedNames.has(s.name.toLowerCase());
      return !hasId && !hasName;
    });
  }, [enrollments, papers]);

  // Filter enrollments for student selection dropdown
  const filteredEnrollments = useMemo(() => {
    const q = studentSearchQuery.trim().toLowerCase();
    if (!q) return enrollments;
    return enrollments.filter((s) => {
      const name = (s.name || "").toLowerCase();
      const roll = (s.rollNo || s.studentId || "").toLowerCase();
      const email = (s.email || "").toLowerCase();
      return name.includes(q) || roll.includes(q) || email.includes(q);
    });
  }, [enrollments, studentSearchQuery]);

  // Counts
  const autoGradedPapersCount = useMemo(() => {
    return papers.filter((p) => isPaperAutoGraded(p)).length;
  }, [papers]);

  const readyPapersCount = useMemo(() => {
    return papers.filter((p) => isPaperReady(p)).length;
  }, [papers]);

  const reviewPapersCount = useMemo(() => {
    return papers.filter((p) => isPaperReview(p)).length;
  }, [papers]);

  // Filter papers for Vertical List based on Tabs & Search Query
  const filteredPapers = useMemo(() => {
    return papers
      .map((paper, originalIndex) => ({ paper, originalIndex }))
      .filter(({ paper, originalIndex }) => {
        const isAutoGraded = isPaperAutoGraded(paper);
        const ready = isPaperReady(paper);
        const needsReview = isPaperReview(paper);

        if (filterTab === "ready" && !ready) return false;
        if (filterTab === "review" && !needsReview) return false;
        if (filterTab === "graded" && !isAutoGraded) return false;

        if (paperSearchQuery.trim()) {
          const q = paperSearchQuery.toLowerCase();
          const name = (
            paper.mappedStudentName ||
            paper.extractedStudentName ||
            ""
          ).toLowerCase();
          const roll = (getPaperRollNo(paper) || "").toLowerCase();
          const refId = String(paper.extractedRollNo || "").toLowerCase();
          const title = `answer paper ${originalIndex + 1}`.toLowerCase();
          const pageNums = (paper.pageNumbers || []).join(" ");
          return (
            name.includes(q) ||
            roll.includes(q) ||
            refId.includes(q) ||
            title.includes(q) ||
            pageNums.includes(q)
          );
        }
        return true;
      });
  }, [papers, filterTab, paperSearchQuery, getPaperRollNo]);

  // Explicit Save Changes Handler
  const handleSaveChanges = async () => {
    if (!activeTestId || isSaving) return;
    setIsSaving(true);
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || "";
      const res = await fetchWithAuth(
        `${serverUrl}/api/smart-split/${activeTestId}/papers`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ papers }),
        },
      );

      if (!res || !res.ok) {
        const errData = res ? await res.json().catch(() => ({})) : {};
        throw new Error(errData.message || "Failed to save paper changes.");
      }

      setHistory([]);
      onSave?.(papers);
      onPaperUpdated?.(papers);

      setUndoToast({
        message: "✓ All Answer Paper changes saved to database.",
        timestamp: Date.now(),
      });
    } catch (err) {
      console.error("[SmartSplitViewer] Save error:", err);
      setUndoToast({
        message: `Failed to save: ${err.message}`,
        timestamp: Date.now(),
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Eligible matched papers ready to be uploaded
  const eligibleMatchedPapers = useMemo(() => {
    return papers.filter(
      (p) =>
        isPaperReady(p) &&
        p.mappedStudentUserId &&
        !p.answerPaperId &&
        !p.isSubmitted &&
        p.pageNumbers &&
        p.pageNumbers.length > 0,
    );
  }, [papers]);


  // Slices specific page numbers into a lightweight PDF file
  const createStudentPdfFile = async (pageNumbers, studentName, rollNo) => {
    const buffer = await getMasterPdfBuffer();
    const sourcePdf = await PDFDocument.load(buffer, {
      ignoreEncryption: true,
    });
    const studentPdf = await PDFDocument.create();

    const pageIndices = pageNumbers
      .map((pn) => pn - 1)
      .filter((idx) => idx >= 0 && idx < sourcePdf.getPageCount());

    if (pageIndices.length === 0) {
      throw new Error("No valid page numbers found in scan.");
    }

    const copiedPages = await studentPdf.copyPages(sourcePdf, pageIndices);
    copiedPages.forEach((page, i) => {
      const pn = pageNumbers[i];
      const rot = pageRotations[pn];
      if (rot !== undefined && rot !== null) {
        page.setRotation(degrees(rot % 360));
      }
      studentPdf.addPage(page);
    });

    const pdfBytes = await studentPdf.save();
    const safeName = (studentName || rollNo || "student").replace(
      /[^a-zA-Z0-9_-]/g,
      "_",
    );
    const fileName = `Assessment_${safeName}.pdf`;
    return new File([pdfBytes], fileName, { type: "application/pdf" });
  };

  // Sequential batch upload of all eligible matched papers
  const handleExecuteBatchUpload = async () => {
    if (eligibleMatchedPapers.length === 0 || isBatchUploading) return;

    setIsBatchUploading(true);
    uploadAbortControllerRef.current = new AbortController();
    const signal = uploadAbortControllerRef.current.signal;

    setBatchModalState({
      phase: "uploading",
      currentIdx: 0,
      total: eligibleMatchedPapers.length,
      currentName:
        eligibleMatchedPapers[0]?.mappedStudentName || "Preparing...",
      errors: [],
      successCount: 0,
    });

    const serverUrl = process.env.REACT_APP_SERVER_URL || "";
    let currentPapers = [...papers];
    const errors = [];
    let successCount = 0;

    try {
      await getMasterPdfBuffer();

      // Fetch scan metadata for batch tracking and offset fallback
      let nextBatchOrder = 1;
      let liveMaxScanOrder = 0;
      try {
        const metaRes = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/scan-meta/${activeTestId}`,
          { signal },
        );
        if (metaRes?.ok) {
          const metaData = await metaRes.json();
          nextBatchOrder = metaData.nextBatchOrder || 1;
          liveMaxScanOrder = metaData.maxScanOrder || 0;
        }
      } catch (metaErr) {
        console.warn("[SmartSplitViewer] Failed to fetch scan-meta:", metaErr);
      }

      for (let i = 0; i < eligibleMatchedPapers.length; i++) {
        if (signal.aborted) throw new Error("Upload aborted by user.");

        const paper = eligibleMatchedPapers[i];
        const studentName = paper.mappedStudentName || `Student ${i + 1}`;

        setBatchModalState((prev) => ({
          ...prev,
          currentIdx: i + 1,
          currentName: studentName,
        }));

        try {
          const studentRoll =
            paper.mappedStudentRollNo ||
            paper.mappedRollNo ||
            getPaperRollNo(paper);
          const file = await createStudentPdfFile(
            paper.pageNumbers,
            studentName,
            studentRoll,
          );

          if (signal.aborted) throw new Error("Upload aborted by user.");

          const paperOriginalIdx = currentPapers.findIndex(
            (p) =>
              p === paper ||
              (paper.pageNumbers?.[0] !== undefined &&
                p.pageNumbers?.[0] === paper.pageNumbers?.[0]) ||
              (p.mappedStudentUserId &&
                paper.mappedStudentUserId &&
                String(p.mappedStudentUserId) ===
                  String(paper.mappedStudentUserId)),
          );

          let scanOrderOffset = smartSplitResult?.scanOrderOffset || 0;
          const hasAnySubmittedInViewer = currentPapers.some(
            (p) => p.isSubmitted || p.answerPaperId,
          );
          if (scanOrderOffset === 0 && !hasAnySubmittedInViewer && liveMaxScanOrder > 0) {
            scanOrderOffset = liveMaxScanOrder;
          }

          const scanOrder =
            paperOriginalIdx !== -1
              ? scanOrderOffset + paperOriginalIdx + 1
              : null;

          const formData = new FormData();
          formData.append("studentId", paper.mappedStudentUserId);
          formData.append("testId", activeTestId);
          formData.append("numberOfPages", paper.pageNumbers.length);
          if (scanOrder !== null && scanOrder !== undefined) {
            formData.append("scanOrder", scanOrder);
          }
          if (nextBatchOrder !== null && nextBatchOrder !== undefined) {
            formData.append("batchOrder", nextBatchOrder);
          }
          formData.append("file", file);

          const res = await fetchWithAuth(`${serverUrl}/api/answer-paper/add`, {
            method: "POST",
            body: formData,
            signal,
          });

          if (!res || !res.ok) {
            const errData = await res?.json().catch(() => ({}));
            throw new Error(
              errData?.message || "Failed to create answer paper submission.",
            );
          }

          const created = await res.json();
          successCount++;

          if (paperOriginalIdx !== -1) {
            currentPapers[paperOriginalIdx] = {
              ...currentPapers[paperOriginalIdx],
              answerPaperId: created._id,
              isSubmitted: true,
            };
          }
        } catch (studentErr) {
          if (studentErr.name === "AbortError" || signal.aborted) {
            throw studentErr;
          }
          console.error(
            `[SmartSplit] Error uploading for ${studentName}:`,
            studentErr,
          );
          errors.push({
            name: studentName,
            error: studentErr.message,
          });
        }
      }

      setPapers(currentPapers);

      if (activeTestId && successCount > 0) {
        try {
          await fetchWithAuth(
            `${serverUrl}/api/smart-split/${activeTestId}/papers`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ papers: currentPapers }),
            },
          );
          onSave?.(currentPapers);
          onPaperUpdated?.(currentPapers);
          onSubmissionsUpdated?.();
        } catch (saveErr) {
          console.error(
            "[SmartSplit] Error persisting updated papers after batch upload:",
            saveErr,
          );
        }
      }

      if (successCount > 0 && errors.length === 0) {
        setBatchModalState(null);
        setHistory([]);
        onClose();
        swalSuccess(
          `Successfully uploaded and submitted ${successCount} answer paper(s)!`,
          { title: "Upload Complete", timer: 2000 },
        );
      } else {
        setBatchModalState({
          phase: "done",
          currentIdx: eligibleMatchedPapers.length,
          total: eligibleMatchedPapers.length,
          successCount,
          errors,
        });
      }
    } catch (abortOrFatalErr) {
      console.warn(
        "[SmartSplit] Batch upload terminated:",
        abortOrFatalErr.message,
      );
      if (successCount > 0) {
        setPapers(currentPapers);
        onSave?.(currentPapers);
        onPaperUpdated?.(currentPapers);
        onSubmissionsUpdated?.();
      }
      setBatchModalState({
        phase: "done",
        currentIdx: successCount,
        total: eligibleMatchedPapers.length,
        successCount,
        errors: [
          ...errors,
          { name: "Process", error: abortOrFatalErr.message },
        ],
      });
    } finally {
      setIsBatchUploading(false);
    }
  };

  // Reset transient modal states when viewer opens/closes
  useEffect(() => {
    if (!isOpen) {
      setBatchModalState(null);
      setHistory([]);
    }
  }, [isOpen]);

  // Close Guard
  const handleAttemptClose = () => {
    if (isDirty) {
      setShowCloseGuard(true);
    } else {
      onClose();
    }
  };

  // Auto-dismiss Undo toast after 6s
  useEffect(() => {
    if (!undoToast) return;
    const timer = setTimeout(() => {
      setUndoToast(null);
    }, 6000);
    return () => clearTimeout(timer);
  }, [undoToast]);

  const activePaperData = papers[activePaper];
  const activePaperIsReady = isPaperReady(activePaperData);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleAttemptClose}
      maxWidth="99vw"
      padding="0"
      modalContentClass="smart-split-modal-content"
      closeButton={false}
    >
      <div className="smart-split-modal-container">
        {/* ─── Top Header Bar ─────────────────────────────────────────────────── */}
        <div className="smart-split-topbar">
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              onClick={handleAttemptClose}
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "0.95rem",
                transition: "all 0.15s ease",
              }}
              title="Close Viewer"
            >
              <i className="fa-solid fa-arrow-left"></i>
            </button>

            <div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <h2
                  style={{
                    fontSize: "1.1rem",
                    margin: 0,
                    fontWeight: 700,
                    color: "#0f172a",
                    lineHeight: "1.2",
                  }}
                >
                  Review &amp; Assign Answer Papers
                </h2>
                <span
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    background: "#f1f5f9",
                    color: "#475569",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {smartSplitResult?.fileName || "Merged_Scans.pdf"}
                </span>
              </div>
              <div
                style={{
                  margin: "3px 0 0 0",
                  fontSize: "0.76rem",
                  color: "#64748b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>
                  <strong>{papers.length}</strong> Answer Papers
                </span>
                {autoGradedPapersCount > 0 && (
                  <>
                    <span>·</span>
                    <span
                      style={{
                        color: "#2563eb",
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <i className="fa-solid fa-bolt" />
                      {autoGradedPapersCount} Graded
                    </span>
                  </>
                )}
                <span>·</span>

                <span
                  style={{
                    color: "#16a34a",
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <i className="fa-solid fa-circle-check" />
                  {readyPapersCount} Ready
                </span>
                {reviewPapersCount > 0 && (
                  <>
                    <span>·</span>
                    <span
                      style={{
                        color: "#dc2626",
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <i className="fa-solid fa-triangle-exclamation" />
                      {reviewPapersCount} Need Review
                    </span>
                  </>
                )}
                {unsubmittedStudents.length > 0 && (
                  <>
                    <span>·</span>
                    <button
                      onClick={() => setShowAbsentDrawer((v) => !v)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        color: "#b45309",
                        fontWeight: 600,
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontSize: "0.76rem",
                      }}
                    >
                      {unsubmittedStudents.length} Absent Students
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Header Action Buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {/* Download Answer Sheet */}
            {smartSplitResult?.masterPdfUrl && (
              <a
                href={smartSplitResult.masterPdfUrl}
                download={smartSplitResult.fileName || "scanned_papers.pdf"}
                target="_blank"
                rel="noreferrer"
                style={{
                  padding: "7px 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#334155",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  textDecoration: "none",
                  transition: "all 0.15s ease",
                }}
              >
                <i
                  className="fa-solid fa-download"
                  style={{ fontSize: "0.76rem" }}
                />
                <span>Download Answer Sheet</span>
              </a>
            )}

            {/* Save Changes Button */}
            {isDirty ? (
              <button
                onClick={handleSaveChanges}
                disabled={isSaving}
                style={{
                  padding: "7px 16px",
                  borderRadius: "6px",
                  border: "none",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  cursor: isSaving ? "not-allowed" : "pointer",
                  background: "#16a34a",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  boxShadow: "0 2px 6px rgba(22, 163, 74, 0.25)",
                  transition: "all 0.15s ease",
                }}
              >
                {isSaving ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-floppy-disk" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            ) : (
              <div
                style={{
                  fontSize: "0.76rem",
                  color: "#64748b",
                  padding: "5px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <i
                  className="fa-solid fa-check"
                  style={{ color: "#16a34a", fontSize: "0.85rem" }}
                />
                <span>Saved</span>
              </div>
            )}

            {/* Upload Answer Papers */}
            <button
              onClick={() => setBatchModalState({ phase: "confirm" })}
              disabled={
                isDirty ||
                isSaving ||
                isBatchUploading ||
                eligibleMatchedPapers.length === 0
              }
              style={{
                padding: "7px 18px",
                borderRadius: "6px",
                border: "none",
                fontSize: "0.82rem",
                fontWeight: 700,
                cursor:
                  !isDirty &&
                  !isSaving &&
                  !isBatchUploading &&
                  eligibleMatchedPapers.length > 0
                    ? "pointer"
                    : "not-allowed",
                background:
                  !isDirty && eligibleMatchedPapers.length > 0
                    ? "#0f172a"
                    : "#cbd5e1",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                boxShadow:
                  !isDirty && eligibleMatchedPapers.length > 0
                    ? "0 2px 8px rgba(15, 23, 42, 0.15)"
                    : "none",
                transition: "all 0.15s ease",
              }}
              title={
                isDirty
                  ? "Please save changes first"
                  : eligibleMatchedPapers.length === 0
                    ? "No matched Answer Papers ready for upload"
                    : `Upload ${eligibleMatchedPapers.length} matched Answer Paper(s)`
              }
            >
              <i className="fa-solid fa-cloud-arrow-up" />
              <span>Upload ({eligibleMatchedPapers.length})</span>
            </button>
          </div>
        </div>

        {/* ─── Mobile View Switcher (Only visible on screens <= 768px via CSS) ─ */}
        <div className="smart-split-mobile-tab-bar">
          <button
            type="button"
            className={`smart-split-mobile-tab-btn ${mobileTab === "papers" ? "active" : ""}`}
            onClick={() => setMobileTab("papers")}
          >
            <i className="fa-solid fa-layer-group" />
            <span>Answer Papers ({papers.length})</span>
            {reviewPapersCount > 0 && (
              <span className="smart-split-mobile-tab-badge review">
                {reviewPapersCount}
              </span>
            )}
          </button>
          <button
            type="button"
            className={`smart-split-mobile-tab-btn ${mobileTab === "preview" ? "active" : ""}`}
            onClick={() => setMobileTab("preview")}
          >
            <i className="fa-regular fa-file-lines" />
            <span>
              Preview #{activePaper + 1}
              {activePaperData?.mappedStudentName
                ? ` (${activePaperData.mappedStudentName.split(" ")[0]})`
                : ""}
            </span>
            {activePaperIsReady ? (
              <span className="smart-split-mobile-tab-badge ready">
                <i className="fa-solid fa-check" />
              </span>
            ) : (
              <span className="smart-split-mobile-tab-badge review">!</span>
            )}
          </button>
        </div>

        {/* ─── Main Workspace: Left Vertical Answer Papers Sidebar + Right Document Canvas ─ */}
        <div className={`smart-split-workspace mobile-tab-${mobileTab}`}>
          {/* ══════════════════════════════════════════════════════════════════════
              LEFT SIDEBAR: Vertical Answer Papers List (Focus of the View)
              ══════════════════════════════════════════════════════════════════════ */}
          <div className="smart-split-papers-sidebar">
            {/* Sidebar Top: Filter Tabs & Search */}
            <div className="smart-split-sidebar-header">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    color: "#0f172a",
                  }}
                >
                  All Answer Papers ({papers.length})
                </span>
                {unsubmittedStudents.length > 0 && (
                  <button
                    onClick={() => setShowAbsentDrawer((v) => !v)}
                    style={{
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                      color: "#b45309",
                      padding: "2px 8px",
                      borderRadius: "5px",
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {showAbsentDrawer
                      ? "Hide Absent"
                      : `Absent (${unsubmittedStudents.length})`}
                  </button>
                )}
              </div>

              {/* Clear Filter Tabs */}
              <div className="smart-split-filter-tabs">
                <button
                  className={`smart-split-filter-btn ${filterTab === "all" ? "active" : ""}`}
                  onClick={() => setFilterTab("all")}
                >
                  All ({papers.length})
                </button>
                <button
                  className={`smart-split-filter-btn filter-review ${filterTab === "review" ? "active" : ""}`}
                  onClick={() => setFilterTab("review")}
                >
                  <i
                    className="fa-solid fa-triangle-exclamation"
                    style={{ color: "#dc2626" }}
                  />
                  Review ({reviewPapersCount})
                </button>
                <button
                  className={`smart-split-filter-btn filter-ready ${filterTab === "ready" ? "active" : ""}`}
                  onClick={() => setFilterTab("ready")}
                >
                  <i
                    className="fa-solid fa-circle-check"
                    style={{ color: "#16a34a" }}
                  />
                  Ready ({readyPapersCount})
                </button>
                {autoGradedPapersCount > 0 && (
                  <button
                    className={`smart-split-filter-btn filter-graded ${filterTab === "graded" ? "active" : ""}`}
                    onClick={() => setFilterTab("graded")}
                    style={
                      filterTab === "graded"
                        ? {
                            background: "#eff6ff",
                            color: "#1d4ed8",
                            borderColor: "#bfdbfe",
                          }
                        : undefined
                    }
                  >
                    <i
                      className="fa-solid fa-bolt"
                      style={{ color: "#2563eb" }}
                    />
                    Graded ({autoGradedPapersCount})
                  </button>
                )}
              </div>

              {/* Search Box */}
              <div className="smart-split-search-box">
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{ color: "#94a3b8", fontSize: "0.75rem" }}
                />
                <input
                  type="text"
                  placeholder="Search student name, roll, or paper #..."
                  value={paperSearchQuery}
                  onChange={(e) => setPaperSearchQuery(e.target.value)}
                />
                {paperSearchQuery && (
                  <button
                    onClick={() => setPaperSearchQuery("")}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#94a3b8",
                      cursor: "pointer",
                      fontSize: "0.75rem",
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Absent Drawer inside Left Sidebar */}
              {showAbsentDrawer && unsubmittedStudents.length > 0 && (
                <div
                  style={{
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: "7px",
                    padding: "8px 10px",
                    maxHeight: "130px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "#92400e",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>
                      Unmatched Enrolled Students ({unsubmittedStudents.length})
                    </span>
                    <button
                      onClick={() => setShowAbsentDrawer(false)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#92400e",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  {unsubmittedStudents.map((s, si) => (
                    <div
                      key={si}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: "#ffffff",
                        padding: "3px 6px",
                        borderRadius: "4px",
                        border: "1px solid #fef08a",
                        fontSize: "0.72rem",
                      }}
                    >
                      <span style={{ fontWeight: 600, color: "#1e293b" }}>
                        {s.name}
                      </span>
                      <button
                        onClick={() => handleAssignStudentClick(s, activePaper)}
                        style={{
                          padding: "2px 7px",
                          borderRadius: "3px",
                          border: "none",
                          background: "#3b82f6",
                          color: "#ffffff",
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Assign to Paper #{activePaper + 1}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Vertical Scrollable Papers List */}
            <div className="smart-split-papers-list">
              {filteredPapers.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 10px",
                    color: "#94a3b8",
                  }}
                >
                  <i
                    className="fa-solid fa-magnifying-glass"
                    style={{ fontSize: "1.8rem", marginBottom: "8px" }}
                  />
                  <p
                    style={{ margin: 0, fontSize: "0.85rem", fontWeight: 600 }}
                  >
                    No Answer Papers match filter
                  </p>
                </div>
              ) : (
                filteredPapers.map(({ paper, originalIndex: pIdx }) => {
                  const isActive = activePaper === pIdx;
                  const isAutoGraded = isPaperAutoGraded(paper);
                  const isReady = isPaperReady(paper);
                  const studentDisplay = paper.mappedStudentName || null;
                  const rollDisplay = getPaperRollNo(paper) || null;

                  return (
                    <div
                      key={pIdx}
                      id={`smart-split-card-${pIdx}`}
                      className={`smart-split-paper-card ${isAutoGraded ? "auto-graded" : isReady ? "ready" : "needs-review"} ${isActive ? "active" : ""}`}
                      style={{
                        outline:
                          dragOverTarget?.targetPaperIdx === pIdx &&
                          dragOverTarget?.targetPageIndex === undefined
                            ? "2px dashed #3b82f6"
                            : undefined,
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverTarget({ targetPaperIdx: pIdx });
                      }}
                      onDragLeave={(e) => {
                        e.stopPropagation();
                        if (
                          dragOverTarget?.targetPaperIdx === pIdx &&
                          dragOverTarget?.targetPageIndex === undefined
                        ) {
                          setDragOverTarget(null);
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverTarget(null);
                        if (draggedPage) {
                          handlePageDrop(
                            draggedPage.sourcePaperIdx,
                            draggedPage.pageNumber,
                            pIdx,
                            paper.pageNumbers?.length || 0,
                          );
                          setDraggedPage(null);
                        }
                      }}
                      onClick={() => {
                        setActivePaper(pIdx);
                        if (paper.pageNumbers?.[0]) {
                          setActivePageNumber(paper.pageNumbers[0]);
                        }
                        if (typeof window !== "undefined" && window.innerWidth <= 768) {
                          setMobileTab("preview");
                        }
                      }}
                    >
                      {/* Card Header: Title & Status Indicator */}
                      <div className="smart-split-card-header">
                        <div className="smart-split-card-title">
                          <span
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              background: isAutoGraded
                                ? "#2563eb"
                                : isReady
                                  ? "#16a34a"
                                  : "#dc2626",
                              display: "inline-block",
                              flexShrink: 0,
                            }}
                          />
                          <span>Answer Paper #{pIdx + 1}</span>
                          {isActive && (
                            <span
                              style={{
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                background: "#eff6ff",
                                color: "#2563eb",
                                padding: "1px 5px",
                                borderRadius: "4px",
                                border: "1px solid #bfdbfe",
                              }}
                            >
                              Active
                            </span>
                          )}
                        </div>

                        {/* Badges: Graded (Blue), Ready (Green Check), or Needs Review (Red/Amber) */}
                        {isAutoGraded ? (
                          <span
                            className="smart-split-badge-ready"
                            style={{
                              background: "#eff6ff",
                              color: "#1d4ed8",
                              border: "1px solid #bfdbfe",
                            }}
                          >
                            <i className="fa-solid fa-bolt" />
                            Graded
                          </span>
                        ) : isReady ? (
                          <span className="smart-split-badge-ready">
                            <i className="fa-solid fa-circle-check" />
                            Ready
                          </span>
                        ) : paper.pageAnomaly?.hasAnomaly && !paper.isAnomalyDismissed ? (
                          <span
                            className="smart-split-badge-review"
                            style={{
                              background: "#fffbeb",
                              color: "#b45309",
                              borderColor: "#fde68a",
                              fontSize: "0.68rem",
                              padding: "2px 7px",
                            }}
                            title={paper.pageAnomaly.teacherTitle}
                          >
                            <i
                              className="fa-solid fa-triangle-exclamation"
                              style={{ color: "#d97706" }}
                            />
                            {paper.pageAnomaly.shortBadge || "Needs Review"}
                          </span>
                        ) : (
                          <span className="smart-split-badge-review">
                            <i className="fa-solid fa-triangle-exclamation" />
                            Needs Review
                          </span>
                        )}
                      </div>

                      {/* Card Student Information */}
                      <div className="smart-split-card-student">
                        <div
                          className={`smart-split-student-name ${!studentDisplay ? "unassigned" : ""}`}
                        >
                          {studentDisplay ||
                            "⚠️ Not Assigned — Click to Select"}
                        </div>

                        <div className="smart-split-card-meta">
                          {rollDisplay && (
                            <span className="smart-split-meta-pill">
                              Roll No.: {rollDisplay}
                            </span>
                          )}
                          {paper.extractedRollNo && (
                            <span
                              className="smart-split-meta-pill"
                              style={{
                                background: "#f8fafc",
                                color: "#475569",
                                border: "1px solid #e2e8f0",
                              }}
                              title={`Scanned OMR Reference ID: ${paper.extractedRollNo}`}
                            >
                              Ref ID: #{paper.extractedRollNo}
                            </span>
                          )}
                          <span
                            className="smart-split-meta-pill"
                            style={
                              paper.pageAnomaly?.hasAnomaly && !paper.isAnomalyDismissed
                                ? { background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700 }
                                : undefined
                            }
                          >
                            {paper.pageNumbers?.length || 0} pgs
                          </span>
                          {paper.extractedStudentName && (
                            <span
                              style={{
                                fontSize: "0.68rem",
                                color: "#64748b",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "200px",
                              }}
                              title={`Identified name: ${paper.extractedStudentName}`}
                            >
                              Identified name: &ldquo;
                              {paper.extractedStudentName}&rdquo;
                            </span>
                          )}
                        </div>

                        {paper.pageAnomaly?.hasAnomaly &&
                          !paper.isAnomalyDismissed &&
                          !paper.isSubmitted &&
                          !paper.answerPaperId && (
                            <div
                              style={{
                                fontSize: "0.68rem",
                                color: "#92400e",
                                marginTop: "4px",
                                background: "#fffbeb",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                border: "1px solid #fde68a",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              title={paper.pageAnomaly.teacherMessage}
                            >
                              <i
                                className="fa-solid fa-triangle-exclamation"
                                style={{ fontSize: "0.65rem", color: "#d97706", flexShrink: 0 }}
                              />
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontWeight: 600,
                                }}
                              >
                                {paper.pageAnomaly.teacherTitle}
                              </span>
                            </div>
                          )}

                        {paper.statusMessage &&
                          !paper.pageAnomaly?.hasAnomaly &&
                          !paper.isSubmitted &&
                          !paper.answerPaperId &&
                          !isReady && (
                            <div
                              style={{
                                fontSize: "0.68rem",
                                color: "#b91c1c",
                                marginTop: "4px",
                                background: "#fef2f2",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                border: "1px solid #fee2e2",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              title={paper.statusMessage}
                            >
                              <i
                                className="fa-solid fa-circle-exclamation"
                                style={{ fontSize: "0.65rem", flexShrink: 0 }}
                              />
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {paper.statusMessage}
                              </span>
                            </div>
                          )}

                        {paper.suggestedStudentName &&
                          !paper.mappedStudentUserId && (
                            <div
                              style={{
                                marginTop: "5px",
                                padding: "4px 8px",
                                background: "#eff6ff",
                                border: "1px solid #bfdbfe",
                                borderRadius: "5px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                fontSize: "0.72rem",
                                gap: "6px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  color: "#1e40af",
                                  overflow: "hidden",
                                }}
                              >
                                <i
                                  className="fa-solid fa-lightbulb"
                                  style={{ color: "#2563eb", flexShrink: 0 }}
                                />
                                <span
                                  style={{
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Suggested:{" "}
                                  <strong>{paper.suggestedStudentName}</strong>
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const matched = enrollments.find(
                                    (s) =>
                                      (paper.suggestedStudentUserId &&
                                        String(s.userId || s._id) ===
                                          String(
                                            paper.suggestedStudentUserId,
                                          )) ||
                                      (s.name &&
                                        s.name.trim().toLowerCase() ===
                                          paper.suggestedStudentName
                                            .trim()
                                            .toLowerCase()),
                                  );
                                  if (matched) {
                                    applyStudentAssignment(matched, pIdx);
                                  } else {
                                    applyStudentAssignment(
                                      {
                                        name: paper.suggestedStudentName,
                                        userId: paper.suggestedStudentUserId,
                                      },
                                      pIdx,
                                    );
                                  }
                                }}
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: "3px",
                                  border: "none",
                                  background: "#2563eb",
                                  color: "#ffffff",
                                  fontSize: "0.68rem",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                                title={`Assign ${paper.suggestedStudentName}`}
                              >
                                Assign
                              </button>
                            </div>
                          )}
                      </div>

                      {/* Page Thumbnails Row for this Paper */}
                      <div className="smart-split-thumbs-row">
                        {paper.pageNumbers?.map((pn, pi) => {
                          const isActiveThumb =
                            activePageNumber === pn && isActive;
                          const isDragOverThis =
                            dragOverTarget?.targetPaperIdx === pIdx &&
                            dragOverTarget?.targetPageIndex === pi;
                          const dropPos = isDragOverThis
                            ? dragOverTarget.position
                            : null;

                          return (
                            <div
                              key={pn}
                              className={`smart-split-thumb-card ${isActiveThumb ? "active-thumb" : ""}`}
                              style={{
                                boxShadow:
                                  dropPos === "before"
                                    ? "-4px 0 0 0 #2563eb"
                                    : dropPos === "after"
                                      ? "4px 0 0 0 #2563eb"
                                      : undefined,
                                transform: isDragOverThis
                                  ? "scale(1.03)"
                                  : undefined,
                                transition:
                                  "transform 0.1s ease, box-shadow 0.1s ease",
                              }}
                              draggable={!isAutoGraded}
                              onDragStart={(e) => {
                                if (isAutoGraded) {
                                  e.preventDefault();
                                  return;
                                }
                                e.stopPropagation();
                                setDraggedPage({
                                  sourcePaperIdx: pIdx,
                                  pageNumber: pn,
                                });
                                e.dataTransfer.setData(
                                  "text/plain",
                                  JSON.stringify({ pIdx, pn }),
                                );
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                const isRightHalf =
                                  e.clientX - rect.left > rect.width / 2;
                                setDragOverTarget({
                                  targetPaperIdx: pIdx,
                                  targetPageIndex: pi,
                                  position: isRightHalf ? "after" : "before",
                                });
                              }}
                              onDragLeave={(e) => {
                                e.stopPropagation();
                                if (
                                  dragOverTarget?.targetPaperIdx === pIdx &&
                                  dragOverTarget?.targetPageIndex === pi
                                ) {
                                  setDragOverTarget(null);
                                }
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const targetInfo = dragOverTarget;
                                setDragOverTarget(null);
                                if (!draggedPage) return;

                                const targetInsertIndex =
                                  targetInfo?.position === "after"
                                    ? pi + 1
                                    : pi;

                                handlePageDrop(
                                  draggedPage.sourcePaperIdx,
                                  draggedPage.pageNumber,
                                  pIdx,
                                  targetInsertIndex,
                                );
                                setDraggedPage(null);
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActivePaper(pIdx);
                                setActivePageNumber(pn);
                                if (typeof window !== "undefined" && window.innerWidth <= 768) {
                                  setMobileTab("preview");
                                }
                              }}
                              title={`Page #${pn} · Click to preview, drag to reorder`}
                            >
                              <SmartSplitPageCanvas
                                testId={activeTestId}
                                pageNumber={pn}
                                isThumbnail={true}
                                rotation={pageRotations[pn]}
                                pdfDoc={masterPdfJsDoc}
                                pdfLoadError={pdfLoadError}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: "2px",
                                  right: "2px",
                                  background: "rgba(15, 23, 42, 0.82)",
                                  color: "#ffffff",
                                  fontSize: "0.55rem",
                                  fontWeight: 700,
                                  padding: "1px 3px",
                                  borderRadius: "3px",
                                  pointerEvents: "none",
                                }}
                              >
                                #{pn}
                              </div>

                              {/* Scissor (Split) Action: Top-left corner, always visible (for pages after first page) */}
                              {pi > 0 && !isAutoGraded && (
                                <div className="smart-split-thumb-actions">
                                  <button
                                    type="button"
                                    className="smart-split-thumb-btn scissor-btn"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      await handleSplitPaperAtPage(pIdx, pn);
                                    }}
                                    title={`Split into new Answer Paper from page #${pn}`}
                                  >
                                    <i className="fa-solid fa-scissors" />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Card Action Buttons Row */}
                      <div
                        className="smart-split-card-actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isAutoGraded ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "0.72rem",
                              color: "#15803d",
                              background: "#f0fdf4",
                              border: "1px solid #bbf7d0",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              fontWeight: 600,
                              width: "100%",
                            }}
                          >
                            <i
                              className="fa-solid fa-lock"
                              style={{ fontSize: "0.72rem", color: "#16a34a" }}
                            />
                            <span>Auto-Graded &amp; Submitted (Actions Locked)</span>
                          </div>
                        ) : (
                          <>
                            {/* Assign / Change Student */}
                            <button
                              className="smart-split-btn-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (remappingTargetPaperIdx === pIdx) {
                                  setRemappingTargetPaperIdx(null);
                                  setDropdownCoords(null);
                                  return;
                                }
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                const dropdownHeight = 250;
                                const spaceBelow =
                                  window.innerHeight - rect.bottom;
                                const openAbove =
                                  spaceBelow < dropdownHeight &&
                                  rect.top > dropdownHeight;

                                setDropdownCoords({
                                  left: Math.max(
                                    10,
                                    Math.min(
                                      window.innerWidth - 300,
                                      rect.left,
                                    ),
                                  ),
                                  top: openAbove ? undefined : rect.bottom + 6,
                                  bottom: openAbove
                                    ? window.innerHeight - rect.top + 6
                                    : undefined,
                                  placement: openAbove ? "top" : "bottom",
                                });
                                setRemappingTargetPaperIdx(pIdx);
                                setStudentSearchQuery("");
                              }}
                              title={
                                studentDisplay
                                  ? "Change assigned student"
                                  : "Assign a student"
                              }
                            >
                              <i className="fa-solid fa-user-pen" />
                              <span>
                                {studentDisplay ? "Change" : "Assign"}
                              </span>
                            </button>

                            {/* Unassign Student (active when a student is mapped) */}
                            {studentDisplay && (
                              <button
                                className="smart-split-btn-sm unassign-btn"
                                onClick={() =>
                                  setUnassignConfirmState({
                                    paperIdx: pIdx,
                                    studentName: studentDisplay,
                                  })
                                }
                                title={`Unassign ${studentDisplay} from Answer Paper #${pIdx + 1}`}
                              >
                                <i className="fa-solid fa-user-xmark" />
                                <span>Unassign</span>
                              </button>
                            )}

                            {/* Merge with another Answer Paper with actual student data */}
                            {papers.some(
                              (p, idx) =>
                                idx !== pIdx &&
                                !isPaperAutoGraded(p) &&
                                Boolean(p.mappedStudentName),
                            ) && (
                              <button
                                className="smart-split-btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (mergeTargetPaperIdx === pIdx) {
                                    setMergeTargetPaperIdx(null);
                                    setMergeDropdownCoords(null);
                                    return;
                                  }
                                  const rect =
                                    e.currentTarget.getBoundingClientRect();
                                  const dropdownHeight = 250;
                                  const spaceBelow =
                                    window.innerHeight - rect.bottom;
                                  const openAbove =
                                    spaceBelow < dropdownHeight &&
                                    rect.top > dropdownHeight;

                                  setMergeDropdownCoords({
                                    left: Math.max(
                                      10,
                                      Math.min(
                                        window.innerWidth - 300,
                                        rect.left,
                                      ),
                                    ),
                                    top: openAbove
                                      ? undefined
                                      : rect.bottom + 6,
                                    bottom: openAbove
                                      ? window.innerHeight - rect.top + 6
                                      : undefined,
                                    placement: openAbove ? "top" : "bottom",
                                  });
                                  setMergeTargetPaperIdx(pIdx);
                                  setMergeSearchQuery("");
                                }}
                                title={`Merge Answer Paper #${pIdx + 1} into a student's Answer Paper`}
                              >
                                <i className="fa-solid fa-link" />
                                <span>Merge</span>
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════════════
              RIGHT PANEL: Document Canvas & Focused Page Inspector
              ══════════════════════════════════════════════════════════════════════ */}
          <div ref={canvasViewportRef} className="smart-split-canvas-container">
            {currentPaperPages.length === 0 ? (
              <div
                style={{
                  margin: "auto",
                  padding: "40px",
                  textAlign: "center",
                  color: "#64748b",
                  background: "#ffffff",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <i
                  className="fa-solid fa-file-circle-xmark"
                  style={{
                    fontSize: "2.4rem",
                    marginBottom: "12px",
                    color: "#94a3b8",
                  }}
                />
                <h3>No pages in this Answer Paper</h3>
                <p style={{ margin: "4px 0 0 0", fontSize: "0.85rem" }}>
                  Use the Move Page option to add pages to this paper.
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  width: "100%",
                  position: "relative",
                }}
              >
                {/* Sticky Top Summary Bar for Active Answer Paper */}
                <div className="smart-split-paper-summary-bar">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {/* Mobile Back Button: Return to Papers List */}
                    <button
                      type="button"
                      className="smart-split-mobile-back-btn"
                      onClick={() => setMobileTab("papers")}
                      title="Back to Answer Papers List"
                    >
                      <i className="fa-solid fa-arrow-left" />
                      <span>Papers</span>
                    </button>

                    <span
                      style={{
                        fontSize: "0.92rem",
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      Answer Paper #{activePaper + 1}
                    </span>

                    {/* Badges: Graded, Ready, or Needs Review */}
                    {isPaperAutoGraded(activePaperData) ? (
                      <span
                        className="smart-split-badge-ready"
                        style={{
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          border: "1px solid #bfdbfe",
                        }}
                      >
                        <i className="fa-solid fa-bolt" />
                        Graded
                      </span>
                    ) : activePaperIsReady ? (
                      <span className="smart-split-badge-ready">
                        <i className="fa-solid fa-circle-check" />
                        Ready
                      </span>
                    ) : (
                      <span className="smart-split-badge-review">
                        <i className="fa-solid fa-triangle-exclamation" />
                        Needs Review
                      </span>
                    )}

                    <span style={{ color: "#cbd5e1" }}>|</span>

                    <span
                      style={{
                        fontSize: "0.9rem",
                        fontWeight: 700,
                        color: activePaperData?.mappedStudentName
                          ? "#0f172a"
                          : "#dc2626",
                      }}
                    >
                      {activePaperData?.mappedStudentName || "Unassigned"}
                    </span>

                    {getPaperRollNo(activePaperData) && (
                      <span className="smart-split-meta-pill">
                        #{getPaperRollNo(activePaperData)}
                      </span>
                    )}

                    {activePaperData?.extractedRollNo && (
                      <span
                        className="smart-split-meta-pill"
                        style={{
                          background: "#f8fafc",
                          color: "#475569",
                          border: "1px solid #e2e8f0",
                        }}
                        title={`Scanned OMR Reference ID: ${activePaperData.extractedRollNo}`}
                      >
                        Ref ID: #{activePaperData.extractedRollNo}
                      </span>
                    )}

                    {activePaperData?.statusMessage &&
                      !activePaperData?.isSubmitted &&
                      !activePaperData?.answerPaperId &&
                      !activePaperIsReady && (
                        <span
                          style={{
                            fontSize: "0.74rem",
                            color: "#b91c1c",
                            background: "#fef2f2",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            border: "1px solid #fee2e2",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <i className="fa-solid fa-circle-exclamation" />
                          {activePaperData.statusMessage}
                        </span>
                      )}

                    {activePaperData?.suggestedStudentName &&
                      !activePaperData?.mappedStudentUserId && (
                        <div
                          style={{
                            fontSize: "0.76rem",
                            background: "#eff6ff",
                            border: "1px solid #bfdbfe",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span style={{ color: "#1e40af" }}>
                            <i
                              className="fa-solid fa-lightbulb"
                              style={{ color: "#2563eb", marginRight: "4px" }}
                            />
                            Suggested:{" "}
                            <strong>
                              {activePaperData.suggestedStudentName}
                            </strong>
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const matched = enrollments.find(
                                (s) =>
                                  (activePaperData.suggestedStudentUserId &&
                                    String(s.userId || s._id) ===
                                      String(
                                        activePaperData.suggestedStudentUserId,
                                      )) ||
                                  (s.name &&
                                    s.name.trim().toLowerCase() ===
                                      activePaperData.suggestedStudentName
                                        .trim()
                                        .toLowerCase()),
                              );
                              if (matched) {
                                applyStudentAssignment(matched, activePaper);
                              } else {
                                applyStudentAssignment(
                                  {
                                    name: activePaperData.suggestedStudentName,
                                    userId:
                                      activePaperData.suggestedStudentUserId,
                                  },
                                  activePaper,
                                );
                              }
                            }}
                            style={{
                              padding: "1px 6px",
                              borderRadius: "3px",
                              border: "none",
                              background: "#2563eb",
                              color: "#ffffff",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            Assign
                          </button>
                        </div>
                      )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {/* Previous / Next Paper buttons */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      <button
                        disabled={activePaper === 0}
                        onClick={() => navigateToAdjacentPaper(-1)}
                        className="smart-split-ctrl-btn"
                        title="Go to Previous Answer Paper (ArrowLeft)"
                      >
                        <i className="fa-solid fa-chevron-left" />
                        <span>Prev Paper</span>
                      </button>
                      <span
                        style={{
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          padding: "0 4px",
                          color: "#64748b",
                        }}
                      >
                        {activePaper + 1} / {papers.length}
                      </span>
                      <button
                        disabled={activePaper === papers.length - 1}
                        onClick={() => navigateToAdjacentPaper(1)}
                        className="smart-split-ctrl-btn"
                        title="Go to Next Answer Paper (ArrowRight)"
                      >
                        <span>Next Paper</span>
                        <i className="fa-solid fa-chevron-right" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Intelligent Booklet Anomaly Banner (Non-tech, empathetic guidance for educators) */}
                {activePaperData?.pageAnomaly?.hasAnomaly &&
                  !activePaperData?.isAnomalyDismissed &&
                  !activePaperData?.isSubmitted &&
                  !activePaperData?.answerPaperId && (
                    <div
                      style={{
                        background:
                          activePaperData.pageAnomaly.type === "unusually_long"
                            ? "#fffbeb"
                            : activePaperData.pageAnomaly.type === "unusually_short"
                              ? "#fef2f2"
                              : "#f8fafc",
                        borderBottom: `1px solid ${
                          activePaperData.pageAnomaly.type === "unusually_long"
                            ? "#fde68a"
                            : activePaperData.pageAnomaly.type === "unusually_short"
                              ? "#fecaca"
                              : "#e2e8f0"
                        }`,
                        padding: "10px 18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "14px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                          flex: 1,
                          minWidth: "260px",
                        }}
                      >
                        <i
                          className="fa-solid fa-triangle-exclamation"
                          style={{
                            color:
                              activePaperData.pageAnomaly.type === "unusually_long"
                                ? "#d97706"
                                : "#dc2626",
                            fontSize: "1.15rem",
                            marginTop: "2px",
                            flexShrink: 0,
                          }}
                        />
                        <div>
                          <div
                            style={{
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              color: "#1e293b",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            <span>{activePaperData.pageAnomaly.teacherTitle}</span>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                background: "#ffffff",
                                padding: "1px 7px",
                                borderRadius: "4px",
                                border: "1px solid #cbd5e1",
                                color: "#475569",
                              }}
                            >
                              Class Typical: ~{activePaperData.pageAnomaly.expectedPages} pgs
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "0.78rem",
                              color: "#475569",
                              marginTop: "3px",
                              lineHeight: 1.45,
                            }}
                          >
                            {activePaperData.pageAnomaly.teacherMessage}
                          </div>
                          {activePaperData.pageAnomaly.actionTip && (
                            <div
                              style={{
                                fontSize: "0.74rem",
                                color: "#b45309",
                                marginTop: "4px",
                                fontWeight: 600,
                              }}
                            >
                              💡 {activePaperData.pageAnomaly.actionTip}
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexShrink: 0,
                        }}
                      >
                        {activePaperData.pageAnomaly.candidateSplitPage && (
                          <button
                            type="button"
                            onClick={() =>
                              handleSplitPaperAtPage(
                                activePaper,
                                activePaperData.pageAnomaly.candidateSplitPage,
                              )
                            }
                            style={{
                              padding: "5px 12px",
                              borderRadius: "5px",
                              border: "none",
                              background: "#d97706",
                              color: "#ffffff",
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <i className="fa-solid fa-scissors" />
                            Split at Pg #{activePaperData.pageAnomaly.candidateSplitPage}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDismissAnomaly(activePaper)}
                          style={{
                            padding: "5px 12px",
                            borderRadius: "5px",
                            border: "1px solid #cbd5e1",
                            background: "#ffffff",
                            color: "#334155",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="Confirm that this booklet length is intentional and approve it"
                        >
                          <i
                            className="fa-solid fa-check"
                            style={{ color: "#16a34a" }}
                          />
                          Looks Good (Dismiss)
                        </button>
                      </div>
                    </div>
                  )}

                {/* Sticky Page Toolbar */}
                <div className="smart-split-viewer-toolbar">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: "#334155",
                    }}
                  >
                    <span style={{ color: "#0f172a" }}>
                      Page {activePageIdxInPaper + 1} of{" "}
                      {currentPaperPages.length}
                    </span>
                    <span
                      style={{
                        color: "#94a3b8",
                        fontWeight: 500,
                        fontSize: "0.76rem",
                      }}
                    >
                      (Scan #{displayBulkPn})
                    </span>
                  </div>

                  <div className="smart-split-controls-right">
                    {/* Page Stepper: < 1 / 2 > */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      <button
                        disabled={activePageIdxInPaper === 0}
                        onClick={() => {
                          if (activePageIdxInPaper > 0) {
                            setActivePageNumber(
                              currentPaperPages[activePageIdxInPaper - 1],
                            );
                          }
                        }}
                        className="smart-split-ctrl-btn"
                        title="Previous Page in this Paper"
                      >
                        <i className="fa-solid fa-chevron-left" />
                      </button>
                      <span
                        style={{
                          fontSize: "0.76rem",
                          fontWeight: 700,
                          color: "#0f172a",
                          minWidth: "42px",
                          textAlign: "center",
                        }}
                      >
                        {activePageIdxInPaper + 1} / {currentPaperPages.length}
                      </span>
                      <button
                        disabled={
                          activePageIdxInPaper === currentPaperPages.length - 1
                        }
                        onClick={() => {
                          if (
                            activePageIdxInPaper <
                            currentPaperPages.length - 1
                          ) {
                            setActivePageNumber(
                              currentPaperPages[activePageIdxInPaper + 1],
                            );
                          }
                        }}
                        className="smart-split-ctrl-btn"
                        title="Next Page in this Paper"
                      >
                        <i className="fa-solid fa-chevron-right" />
                      </button>
                    </div>

                    <div className="smart-split-ctrl-divider" />

                    {/* Move Page To Another Answer Paper (Easy, UX-Friendly) */}
                    <button
                      disabled={isPaperAutoGraded(activePaperData)}
                      onClick={() =>
                        setMovePageModalState({
                          pageNumber: displayBulkPn,
                          sourcePaperIdx: activePaper,
                        })
                      }
                      className="smart-split-ctrl-btn"
                      style={{
                        background: isPaperAutoGraded(activePaperData)
                          ? "#f1f5f9"
                          : "#eff6ff",
                        color: isPaperAutoGraded(activePaperData)
                          ? "#94a3b8"
                          : "#1d4ed8",
                        borderColor: isPaperAutoGraded(activePaperData)
                          ? "#e2e8f0"
                          : "#bfdbfe",
                        fontWeight: 700,
                        cursor: isPaperAutoGraded(activePaperData)
                          ? "not-allowed"
                          : "pointer",
                      }}
                      title={
                        isPaperAutoGraded(activePaperData)
                          ? "Cannot move pages of an already auto-graded paper"
                          : "Move this page to another Answer Paper"
                      }
                    >
                      <i className="fa-solid fa-arrow-right-to-bracket" />
                      <span>Move Page to...</span>
                    </button>

                    {/* Rotate Button */}
                    <button
                      disabled={isPaperAutoGraded(activePaperData)}
                      onClick={() => handleRotatePage(displayBulkPn)}
                      className="smart-split-ctrl-btn"
                      style={
                        isPaperAutoGraded(activePaperData)
                          ? { opacity: 0.5, cursor: "not-allowed" }
                          : undefined
                      }
                      title={
                        isPaperAutoGraded(activePaperData)
                          ? "Cannot rotate pages of an already auto-graded paper"
                          : "Rotate 90° Clockwise"
                      }
                    >
                      <i className="fa-solid fa-rotate-right" />
                      <span>Rotate</span>
                    </button>

                    {/* Split Answer Paper before this page (if not page 1) */}
                    {activePageIdxInPaper > 0 && (
                      <button
                        onClick={async () => {
                          await handleSplitPaperAtPage(
                            activePaper,
                            displayBulkPn,
                          );
                        }}
                        className="smart-split-ctrl-btn"
                        title="Split this Answer Paper before this page"
                      >
                        <i className="fa-solid fa-scissors" />
                        <span>Split Here</span>
                      </button>
                    )}

                    {/* Merge this Paper with another Answer Paper with actual student data */}
                    {papers.some(
                      (p, idx) =>
                        idx !== activePaper && Boolean(p.mappedStudentName),
                    ) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (mergeTargetPaperIdx === activePaper) {
                            setMergeTargetPaperIdx(null);
                            setMergeDropdownCoords(null);
                            return;
                          }
                          const rect = e.currentTarget.getBoundingClientRect();
                          const dropdownHeight = 250;
                          const spaceBelow = window.innerHeight - rect.bottom;
                          const openAbove =
                            spaceBelow < dropdownHeight &&
                            rect.top > dropdownHeight;

                          setMergeDropdownCoords({
                            left: Math.max(
                              10,
                              Math.min(window.innerWidth - 300, rect.left),
                            ),
                            top: openAbove ? undefined : rect.bottom + 6,
                            bottom: openAbove
                              ? window.innerHeight - rect.top + 6
                              : undefined,
                            placement: openAbove ? "top" : "bottom",
                          });
                          setMergeTargetPaperIdx(activePaper);
                          setMergeSearchQuery("");
                        }}
                        className="smart-split-ctrl-btn"
                        title="Merge this Answer Paper into a student's Answer Paper"
                      >
                        <i className="fa-solid fa-link" />
                        <span>Merge Paper</span>
                      </button>
                    )}

                    <div className="smart-split-ctrl-divider" />

                    {/* Zoom Controls */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      <button
                        onClick={() => setZoom((z) => Math.max(40, z - 15))}
                        className="smart-split-ctrl-btn"
                        title="Zoom Out"
                      >
                        <i className="fa-solid fa-minus" />
                      </button>
                      <span
                        style={{
                          fontSize: "0.74rem",
                          minWidth: "40px",
                          textAlign: "center",
                          fontWeight: 600,
                          color: "#475569",
                        }}
                      >
                        {zoom}%
                      </span>
                      <button
                        onClick={() => setZoom((z) => Math.min(200, z + 15))}
                        className="smart-split-ctrl-btn"
                        title="Zoom In"
                      >
                        <i className="fa-solid fa-plus" />
                      </button>
                      <button
                        onClick={() => setZoom(100)}
                        className="smart-split-ctrl-btn"
                        title="Fit Width (100%)"
                      >
                        Fit
                      </button>
                    </div>
                  </div>
                </div>

                {/* 100% Width Active Page Card Frame */}
                <div
                  className="smart-split-page-wrapper"
                  style={{
                    width: `${zoom}%`,
                    maxWidth: zoom <= 100 ? "100%" : "none",
                    transition: "width 0.15s ease",
                  }}
                >
                  <SmartSplitPageCanvas
                    testId={activeTestId}
                    pageNumber={displayBulkPn}
                    isThumbnail={false}
                    rotation={pageRotations[displayBulkPn]}
                    pdfDoc={masterPdfJsDoc}
                    pdfLoadError={pdfLoadError}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ─── Floating Fixed Student Selection Dropdown Popover ──────────────── */}
        {remappingTargetPaperIdx !== null && dropdownCoords && (
          <div
            ref={remappingRef}
            onClick={(e) => e.stopPropagation()}
            className="smart-split-remapping-dropdown"
            style={{
              position: "fixed",
              top: dropdownCoords.top,
              bottom: dropdownCoords.bottom,
              left: dropdownCoords.left,
              zIndex: 99999,
              width: "280px",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              boxShadow:
                dropdownCoords.placement === "top"
                  ? "0 -10px 30px rgba(0, 0, 0, 0.2)"
                  : "0 10px 30px rgba(0, 0, 0, 0.2)",
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #f1f5f9",
                paddingBottom: "4px",
              }}
            >
              <span
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: "#0f172a",
                }}
              >
                Assign to Answer Paper #{remappingTargetPaperIdx + 1}
              </span>
              <button
                onClick={() => {
                  setRemappingTargetPaperIdx(null);
                  setDropdownCoords(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#94a3b8",
                  cursor: "pointer",
                  padding: "2px 6px",
                  fontSize: "0.9rem",
                  lineHeight: 1,
                }}
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Search Box */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "#f8fafc",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                padding: "5px 8px",
              }}
            >
              <i
                className="fa-solid fa-magnifying-glass"
                style={{ color: "#94a3b8", fontSize: "0.72rem" }}
              />
              <input
                type="text"
                value={studentSearchQuery}
                onChange={(e) => setStudentSearchQuery(e.target.value)}
                placeholder="Search student name..."
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: "0.78rem",
                  width: "100%",
                  background: "transparent",
                  color: "#0f172a",
                }}
                autoFocus
              />
              {studentSearchQuery && (
                <button
                  onClick={() => setStudentSearchQuery("")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#94a3b8",
                    cursor: "pointer",
                    fontSize: "0.75rem",
                    padding: 0,
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Scrollable Student List */}
            <div
              style={{
                maxHeight: "180px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "3px",
              }}
            >
              {filteredEnrollments.map((student, sIdx) => {
                const isCurrent =
                  papers[remappingTargetPaperIdx]?.mappedStudentUserId &&
                  String(
                    papers[remappingTargetPaperIdx].mappedStudentUserId,
                  ) === String(student.userId || student._id);

                return (
                  <button
                    key={sIdx}
                    onClick={() => {
                      handleAssignStudentClick(
                        student,
                        remappingTargetPaperIdx,
                      );
                      setDropdownCoords(null);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "6px 8px",
                      background: isCurrent ? "#f0fdf4" : "#ffffff",
                      border: isCurrent
                        ? "1px solid #86efac"
                        : "1px solid #f1f5f9",
                      borderRadius: "5px",
                      cursor: "pointer",
                      textAlign: "left",
                      fontSize: "0.76rem",
                      transition: "all 0.12s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrent) {
                        e.currentTarget.style.background = "#f8fafc";
                        e.currentTarget.style.borderColor = "#3b82f6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrent) {
                        e.currentTarget.style.background = "#ffffff";
                        e.currentTarget.style.borderColor = "#f1f5f9";
                      }
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#0f172a" }}>
                      {student.name}
                      {isCurrent && (
                        <span
                          style={{
                            color: "#16a34a",
                            fontSize: "0.68rem",
                            marginLeft: "5px",
                          }}
                        >
                          (Current)
                        </span>
                      )}
                    </span>
                    {(student.rollNo || student.studentId) && (
                      <span style={{ color: "#64748b", fontSize: "0.7rem" }}>
                        #{student.rollNo || student.studentId}
                      </span>
                    )}
                  </button>
                );
              })}
              {filteredEnrollments.length === 0 && (
                <div
                  style={{
                    padding: "10px",
                    textAlign: "center",
                    color: "#94a3b8",
                    fontSize: "0.75rem",
                  }}
                >
                  No students found matching &ldquo;{studentSearchQuery}&rdquo;
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Move Page to Another Answer Paper Modal ──────────────────────── */}
        {movePageModalState && (
          <div
            className="smart-split-dialog-overlay"
            onClick={() => setMovePageModalState(null)}
          >
            <div
              className="smart-split-dialog-box"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Move Page #{movePageModalState.pageNumber}
                  </h3>
                  <p
                    style={{
                      margin: "2px 0 0 0",
                      fontSize: "0.8rem",
                      color: "#64748b",
                    }}
                  >
                    Currently in Answer Paper #
                    {movePageModalState.sourcePaperIdx + 1} (
                    {papers[movePageModalState.sourcePaperIdx]
                      ?.mappedStudentName || "Unassigned"}
                    )
                  </p>
                </div>
                <button
                  onClick={() => setMovePageModalState(null)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#94a3b8",
                    cursor: "pointer",
                    fontSize: "1rem",
                  }}
                >
                  ✕
                </button>
              </div>

              <div
                style={{
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                Select the destination Answer Paper:
              </div>

              <div
                style={{
                  maxHeight: "260px",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {/* Option to create a brand new Answer Paper with this page */}
                <div
                  className="smart-split-move-item"
                  style={{ background: "#f0fdf4", borderColor: "#86efac" }}
                  onClick={() =>
                    handleMovePageToPaper(
                      movePageModalState.sourcePaperIdx,
                      movePageModalState.pageNumber,
                      "new",
                    )
                  }
                >
                  <div>
                    <strong style={{ color: "#166534", fontSize: "0.85rem" }}>
                      + Create New Answer Paper
                    </strong>
                    <div style={{ fontSize: "0.74rem", color: "#15803d" }}>
                      Creates Answer Paper #{papers.length + 1} with this page
                    </div>
                  </div>
                  <button
                    style={{
                      padding: "5px 12px",
                      borderRadius: "5px",
                      border: "none",
                      background: "#16a34a",
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "0.76rem",
                      cursor: "pointer",
                    }}
                  >
                    Create &amp; Move
                  </button>
                </div>

                {/* Existing Answer Papers */}
                {papers.map((paper, pIdx) => {
                  const isCurrent = pIdx === movePageModalState.sourcePaperIdx;
                  const isAutoGraded = isPaperAutoGraded(paper);
                  const isReady = isPaperReady(paper);
                  return (
                    <div
                      key={pIdx}
                      className={`smart-split-move-item ${isCurrent ? "is-current" : ""} ${isAutoGraded ? "is-locked" : ""}`}
                      style={isAutoGraded ? { opacity: 0.6, cursor: "not-allowed", background: "#f8fafc" } : undefined}
                      onClick={() => {
                        if (!isCurrent && !isAutoGraded) {
                          handleMovePageToPaper(
                            movePageModalState.sourcePaperIdx,
                            movePageModalState.pageNumber,
                            pIdx
                          );
                        }
                      }}
                    >
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <strong style={{ color: "#0f172a", fontSize: "0.85rem" }}>
                            Answer Paper #${pIdx + 1}
                          </strong>
                          {isAutoGraded ? (
                            <span className="smart-split-badge-ready" style={{ fontSize: "0.68rem", padding: "1px 6px", background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" }}>
                              <i className="fa-solid fa-lock" style={{ fontSize: "0.6rem" }} /> Auto-Graded (Locked)
                            </span>
                          ) : isReady ? (
                            <span className="smart-split-badge-ready" style={{ fontSize: "0.68rem", padding: "1px 6px" }}>
                              Ready
                            </span>
                          ) : (
                            <span className="smart-split-badge-review" style={{ fontSize: "0.68rem", padding: "1px 6px" }}>
                              Review
                            </span>
                          )}
                          {isCurrent && (
                            <span style={{ fontSize: "0.7rem", color: "#64748b" }}>
                              (Current Location)
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "0.76rem", color: "#475569", marginTop: "2px" }}>
                          {paper.mappedStudentName || "Unassigned"} · {paper.pageNumbers?.length || 0} page(s)
                        </div>
                      </div>

                      {!isCurrent && (
                        <button
                          disabled={isAutoGraded}
                          style={{
                            padding: "5px 12px",
                            borderRadius: "5px",
                            border: isAutoGraded ? "1px solid #cbd5e1" : "1px solid #3b82f6",
                            background: isAutoGraded ? "#f1f5f9" : "#eff6ff",
                            color: isAutoGraded ? "#94a3b8" : "#1d4ed8",
                            fontWeight: 700,
                            fontSize: "0.76rem",
                            cursor: isAutoGraded ? "not-allowed" : "pointer",
                          }}
                        >
                          {isAutoGraded ? "Locked" : "Move Here →"}
                        </button>
                      )}
                    </div>                  );
                })}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={() => setMovePageModalState(null)}
                  style={{
                    padding: "7px 14px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#475569",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Floating Fixed Merge Selection Dropdown Popover (Same to Same as Assign UI) ─ */}
        {mergeTargetPaperIdx !== null && mergeDropdownCoords && (
          <div
            ref={mergeDropdownRef}
            onClick={(e) => e.stopPropagation()}
            className="smart-split-merge-dropdown"
            style={{
              position: "fixed",
              top: mergeDropdownCoords.top,
              bottom: mergeDropdownCoords.bottom,
              left: mergeDropdownCoords.left,
              zIndex: 99999,
              width: "280px",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              boxShadow:
                mergeDropdownCoords.placement === "top"
                  ? "0 -10px 30px rgba(0, 0, 0, 0.2)"
                  : "0 10px 30px rgba(0, 0, 0, 0.2)",
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #f1f5f9",
                paddingBottom: "4px",
              }}
            >
              <span
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: "#0f172a",
                }}
              >
                Merge with Answer Paper #{mergeTargetPaperIdx + 1}
              </span>
              <button
                onClick={() => {
                  setMergeTargetPaperIdx(null);
                  setMergeDropdownCoords(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#94a3b8",
                  cursor: "pointer",
                  padding: "2px 6px",
                  fontSize: "0.9rem",
                  lineHeight: 1,
                }}
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Search Box */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "#f8fafc",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                padding: "5px 8px",
              }}
            >
              <i
                className="fa-solid fa-magnifying-glass"
                style={{ color: "#94a3b8", fontSize: "0.72rem" }}
              />
              <input
                type="text"
                value={mergeSearchQuery}
                onChange={(e) => setMergeSearchQuery(e.target.value)}
                placeholder="Search student name..."
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: "0.78rem",
                  width: "100%",
                  background: "transparent",
                  color: "#0f172a",
                }}
                autoFocus
              />
              {mergeSearchQuery && (
                <button
                  onClick={() => setMergeSearchQuery("")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#94a3b8",
                    cursor: "pointer",
                    fontSize: "0.75rem",
                    padding: 0,
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Scrollable Student List */}
            <div
              style={{
                maxHeight: "180px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "3px",
              }}
            >
              {(() => {
                const prevPaper =
                  mergeTargetPaperIdx > 0
                    ? papers[mergeTargetPaperIdx - 1]
                    : null;
                const prevRoll = prevPaper ? getPaperRollNo(prevPaper) : null;
                const prevName = prevPaper?.mappedStudentName || null;
                const q = mergeSearchQuery.trim().toLowerCase();
                const showMergeWithPrev =
                  mergeTargetPaperIdx > 0 &&
                  (!q ||
                    "merge with previous".includes(q) ||
                    "previous".includes(q) ||
                    `paper #${mergeTargetPaperIdx}`.toLowerCase().includes(q) ||
                    (prevName && prevName.toLowerCase().includes(q)) ||
                    (prevRoll && String(prevRoll).toLowerCase().includes(q)));

                return (
                  <>
                    {/* First option: Merge with Previous */}
                    {showMergeWithPrev && (
                      <button
                        onClick={() =>
                          handleMergePapers(
                            mergeTargetPaperIdx,
                            mergeTargetPaperIdx - 1,
                          )
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "6px 8px",
                          background: "#f0f7ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: "5px",
                          cursor: "pointer",
                          textAlign: "left",
                          fontSize: "0.76rem",
                          transition: "all 0.12s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#e0effe";
                          e.currentTarget.style.borderColor = "#3b82f6";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#f0f7ff";
                          e.currentTarget.style.borderColor = "#bfdbfe";
                        }}
                        title={`Merge into Answer Paper #${mergeTargetPaperIdx}${prevName ? ` (${prevName})` : ""}`}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#1d4ed8",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <i
                            className="fa-solid fa-arrow-up"
                            style={{ fontSize: "0.7rem", color: "#2563eb" }}
                          />
                          Merge with Previous
                        </span>
                        {prevRoll ? (
                          <span
                            style={{
                              color: "#2563eb",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          >
                            #{prevRoll}
                          </span>
                        ) : (
                          <span
                            style={{ color: "#64748b", fontSize: "0.7rem" }}
                          >
                            #{mergeTargetPaperIdx}
                          </span>
                        )}
                      </button>
                    )}

                    {mergeCandidatePapers.map(({ paper, pIdx, rollNo }) => (
                      <button
                        key={pIdx}
                        onClick={() =>
                          handleMergePapers(mergeTargetPaperIdx, pIdx)
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "6px 8px",
                          background: "#ffffff",
                          border: "1px solid #f1f5f9",
                          borderRadius: "5px",
                          cursor: "pointer",
                          textAlign: "left",
                          fontSize: "0.76rem",
                          transition: "all 0.12s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f8fafc";
                          e.currentTarget.style.borderColor = "#3b82f6";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#ffffff";
                          e.currentTarget.style.borderColor = "#f1f5f9";
                        }}
                      >
                        <span style={{ fontWeight: 600, color: "#0f172a" }}>
                          {paper.mappedStudentName}
                        </span>
                        {rollNo && (
                          <span
                            style={{ color: "#64748b", fontSize: "0.7rem" }}
                          >
                            #{rollNo}
                          </span>
                        )}
                      </button>
                    ))}

                    {mergeCandidatePapers.length === 0 &&
                      !showMergeWithPrev && (
                        <div
                          style={{
                            padding: "10px",
                            textAlign: "center",
                            color: "#94a3b8",
                            fontSize: "0.75rem",
                          }}
                        >
                          {mergeSearchQuery
                            ? `No students found matching \u201c${mergeSearchQuery}\u201d`
                            : "No other Answer Papers with assigned students"}
                        </div>
                      )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* ─── Duplicate Student Assignment Override Dialog ───────────────────── */}
        {overrideConfirmState && (
          <div
            className="smart-split-dialog-overlay"
            onClick={() => setOverrideConfirmState(null)}
          >
            <div
              className="smart-split-dialog-box"
              style={{ position: "relative" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 'X' Close button */}
              <button
                type="button"
                onClick={() => setOverrideConfirmState(null)}
                aria-label="Cancel"
                title="Cancel"
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  background: "transparent",
                  border: "none",
                  color: "#94a3b8",
                  fontSize: "1.1rem",
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  transition: "color 0.15s, background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#0f172a";
                  e.currentTarget.style.background = "#f1f5f9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#94a3b8";
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "10px",
                    background: "#fef3c7",
                    color: "#d97706",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.25rem",
                    flexShrink: 0,
                  }}
                >
                  <i className="fa-solid fa-triangle-exclamation" />
                </div>
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Student Already Assigned
                  </h3>
                  <p
                    style={{
                      margin: "2px 0 0 0",
                      fontSize: "0.8rem",
                      color: "#64748b",
                    }}
                  >
                    Duplicate assignment conflict detected
                  </p>
                </div>
              </div>

              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "12px 14px",
                  fontSize: "0.84rem",
                  color: "#334155",
                  lineHeight: 1.5,
                }}
              >
                <strong>{overrideConfirmState.student?.name}</strong> is already
                assigned to{" "}
                <strong>
                  Answer Paper #{overrideConfirmState.existingPaperIdx + 1}
                </strong>{" "}
                (
                {papers[overrideConfirmState.existingPaperIdx]?.pageNumbers
                  ?.length || 0}{" "}
                pages).
                <br />
                <br />
                Do you want to reassign{" "}
                <strong>{overrideConfirmState.student?.name}</strong> to{" "}
                <strong>
                  Answer Paper #{overrideConfirmState.targetPaperIdx + 1}
                </strong>{" "}
                instead?
                <br />
                <span style={{ fontSize: "0.78rem", color: "#64748b" }}>
                  (Answer Paper #{overrideConfirmState.existingPaperIdx + 1}{" "}
                  will become Unassigned and marked for review.)
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "4px",
                }}
              >
                <button
                  onClick={() => setOverrideConfirmState(null)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#475569",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    applyStudentAssignment(
                      overrideConfirmState.student,
                      overrideConfirmState.targetPaperIdx,
                      true,
                      overrideConfirmState.existingPaperIdx,
                    )
                  }
                  style={{
                    padding: "8px 18px",
                    borderRadius: "6px",
                    border: "none",
                    background: "#16a34a",
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    boxShadow: "0 2px 6px rgba(22, 163, 74, 0.3)",
                  }}
                >
                  <i className="fa-solid fa-check" />
                  <span>Yes, Reassign Student</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Unassign Student Confirmation Dialog ─────────────────────────── */}
        {unassignConfirmState && (
          <div
            className="smart-split-dialog-overlay"
            onClick={() => setUnassignConfirmState(null)}
          >
            <div
              className="smart-split-dialog-box"
              style={{ maxWidth: "420px", position: "relative" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 'X' Close button */}
              <button
                type="button"
                onClick={() => setUnassignConfirmState(null)}
                aria-label="Cancel"
                title="Cancel"
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  background: "transparent",
                  border: "none",
                  color: "#94a3b8",
                  fontSize: "1.1rem",
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  transition: "color 0.15s, background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#0f172a";
                  e.currentTarget.style.background = "#f1f5f9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#94a3b8";
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <i className="fa-solid fa-xmark" />
              </button>

              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "10px",
                    background: "#fef2f2",
                    color: "#dc2626",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.25rem",
                    flexShrink: 0,
                  }}
                >
                  <i className="fa-solid fa-user-xmark" />
                </div>
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Unassign Student?
                  </h3>
                  <p
                    style={{
                      margin: "2px 0 0 0",
                      fontSize: "0.8rem",
                      color: "#64748b",
                    }}
                  >
                    Answer Paper #{unassignConfirmState.paperIdx + 1}
                  </p>
                </div>
              </div>

              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "12px 14px",
                  fontSize: "0.84rem",
                  color: "#334155",
                  lineHeight: 1.5,
                  marginTop: "8px",
                }}
              >
                Are you sure you want to unassign{" "}
                <strong style={{ color: "#0f172a" }}>
                  {unassignConfirmState.studentName}
                </strong>{" "}
                from{" "}
                <strong>
                  Answer Paper #{unassignConfirmState.paperIdx + 1}
                </strong>
                ?
                <br />
                <br />
                <span style={{ fontSize: "0.78rem", color: "#64748b" }}>
                  This paper will be marked as{" "}
                  <strong style={{ color: "#dc2626" }}>Needs Review</strong>{" "}
                  until a student is assigned. You can reassign or undo this
                  anytime.
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setUnassignConfirmState(null)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#475569",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleUnassignStudent(unassignConfirmState.paperIdx);
                    setUnassignConfirmState(null);
                  }}
                  style={{
                    padding: "8px 18px",
                    borderRadius: "6px",
                    border: "none",
                    background: "#dc2626",
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    boxShadow: "0 2px 6px rgba(220, 38, 38, 0.3)",
                  }}
                >
                  <i className="fa-solid fa-user-xmark" />
                  <span>Yes, Unassign</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Floating Undo Toast ────────────────────────────────────────────── */}
        {undoToast && (
          <div
            style={{
              position: "fixed",
              bottom: "24px",
              left: "50%",
              transform: "translateX(-50%)",
              background: "#ffffff",
              color: "#0f172a",
              padding: "10px 20px",
              borderRadius: "30px",
              border: "1.5px solid #3b82f6",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "14px",
              zIndex: 9999,
              fontSize: "0.82rem",
            }}
          >
            <span style={{ fontWeight: 600 }}>{undoToast.message}</span>
            <button
              onClick={undoLastAction}
              style={{
                background: "#3b82f6",
                border: "none",
                borderRadius: "16px",
                padding: "4px 12px",
                color: "#ffffff",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                fontSize: "0.75rem",
              }}
            >
              <i
                className="fa-solid fa-rotate-left"
                style={{ fontSize: "0.7rem" }}
              />
              <span>Undo (Ctrl+Z)</span>
            </button>
            <button
              onClick={() => setUndoToast(null)}
              style={{
                background: "none",
                border: "none",
                color: "#94a3b8",
                cursor: "pointer",
                padding: "0 2px",
                fontSize: "0.85rem",
              }}
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* ─── Batch Upload Modal ─────────────────────────────────────────────── */}
        {batchModalState && (
          <div className="smart-split-dialog-overlay">
            <div className="smart-split-dialog-box" style={{ width: "480px" }}>
              {batchModalState.phase === "confirm" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "10px",
                        background: "#eff6ff",
                        color: "#3b82f6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.25rem",
                      }}
                    >
                      <i className="fa-solid fa-paper-plane" />
                    </div>
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "1.05rem",
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        Upload Answer Papers
                      </h3>
                      <p
                        style={{
                          margin: "2px 0 0 0",
                          fontSize: "0.8rem",
                          color: "#64748b",
                        }}
                      >
                        Submit matched student answer papers for grading
                      </p>
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      padding: "14px",
                      fontSize: "0.83rem",
                      color: "#334155",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>Matched Answer Papers to Upload:</span>
                      <strong style={{ color: "#16a34a" }}>
                        {eligibleMatchedPapers.length} papers
                      </strong>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>Total Pages to Slice &amp; Upload:</span>
                      <strong>
                        {eligibleMatchedPapers.reduce(
                          (acc, p) => acc + (p.pageNumbers?.length || 0),
                          0,
                        )}{" "}
                        pages
                      </strong>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>Excluded (Unmatched / Needs Review):</span>
                      <span style={{ color: "#dc2626" }}>
                        {papers.length - eligibleMatchedPapers.length} paper(s)
                      </span>
                    </div>
                  </div>

                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.78rem",
                      color: "#64748b",
                      lineHeight: "1.4",
                    }}
                  >
                    Each Answer Paper will be sliced directly in your browser
                    with any rotations applied and uploaded to the
                    student&apos;s record.
                  </p>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                      marginTop: "4px",
                    }}
                  >
                    <button
                      onClick={() => setBatchModalState(null)}
                      style={{
                        padding: "7px 16px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        background: "#ffffff",
                        color: "#475569",
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleExecuteBatchUpload}
                      style={{
                        padding: "7px 18px",
                        borderRadius: "6px",
                        border: "none",
                        background: "#16a34a",
                        color: "#ffffff",
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        boxShadow: "0 2px 8px rgba(22, 163, 74, 0.3)",
                      }}
                    >
                      <i className="fa-solid fa-paper-plane" />
                      <span>Start Upload ({eligibleMatchedPapers.length})</span>
                    </button>
                  </div>
                </>
              )}

              {batchModalState.phase === "uploading" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "10px",
                        background: "#eff6ff",
                        color: "#3b82f6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.25rem",
                      }}
                    >
                      <i className="fa-solid fa-spinner fa-spin" />
                    </div>
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "1.05rem",
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        Uploading Answer Papers...
                      </h3>
                      <p
                        style={{
                          margin: "2px 0 0 0",
                          fontSize: "0.8rem",
                          color: "#64748b",
                        }}
                      >
                        Processing student {batchModalState.currentIdx} of{" "}
                        {batchModalState.total}
                      </p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "8px",
                        background: "#f1f5f9",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${Math.round((batchModalState.currentIdx / batchModalState.total) * 100)}%`,
                          background: "#16a34a",
                          transition: "width 0.2s ease",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "0.76rem",
                        color: "#64748b",
                      }}
                    >
                      <span>
                        Current: <strong>{batchModalState.currentName}</strong>
                      </span>
                      <span>
                        {Math.round(
                          (batchModalState.currentIdx / batchModalState.total) *
                            100,
                        )}
                        %
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "8px",
                    }}
                  >
                    <button
                      onClick={() => uploadAbortControllerRef.current?.abort()}
                      style={{
                        padding: "6px 14px",
                        borderRadius: "6px",
                        border: "1px solid #fecaca",
                        background: "#fef2f2",
                        color: "#dc2626",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        cursor: "pointer",
                      }}
                    >
                      Stop Upload
                    </button>
                  </div>
                </>
              )}

              {batchModalState.phase === "done" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "10px",
                        background:
                          batchModalState.errors?.length === 0
                            ? "#f0fdf4"
                            : "#fefce8",
                        color:
                          batchModalState.errors?.length === 0
                            ? "#16a34a"
                            : "#ca8a04",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.25rem",
                      }}
                    >
                      <i
                        className={
                          batchModalState.errors?.length === 0
                            ? "fa-solid fa-circle-check"
                            : "fa-solid fa-triangle-exclamation"
                        }
                      />
                    </div>
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "1.05rem",
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        Upload Complete!
                      </h3>
                      <p
                        style={{
                          margin: "2px 0 0 0",
                          fontSize: "0.8rem",
                          color: "#64748b",
                        }}
                      >
                        {batchModalState.successCount} answer paper(s)
                        successfully submitted
                      </p>
                    </div>
                  </div>

                  {batchModalState.errors?.length > 0 && (
                    <div
                      style={{
                        background: "#fff5f5",
                        border: "1px solid #fecaca",
                        borderRadius: "8px",
                        padding: "10px 12px",
                        maxHeight: "120px",
                        overflowY: "auto",
                        fontSize: "0.76rem",
                        color: "#991b1b",
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <strong>Errors encountered:</strong>
                      {batchModalState.errors.map((e, ei) => (
                        <div key={ei}>
                          • {e.name}: {e.error}
                        </div>
                      ))}
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "6px",
                    }}
                  >
                    <button
                      onClick={() => {
                        setBatchModalState(null);
                        onClose();
                      }}
                      style={{
                        padding: "7px 20px",
                        borderRadius: "6px",
                        border: "none",
                        background: "#0f172a",
                        color: "#ffffff",
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        cursor: "pointer",
                      }}
                    >
                      Done
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ─── Accidental Close Guard Dialog ──────────────────────────────────── */}
        {showCloseGuard && (
          <div className="smart-split-dialog-overlay">
            <div
              className="smart-split-dialog-box"
              style={{ maxWidth: "420px", position: "relative" }}
            >
              {/* 'X' Close button to dismiss and stay on current screen */}
              <button
                type="button"
                onClick={() => setShowCloseGuard(false)}
                aria-label="Stay on current screen"
                title="Stay on current screen"
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  background: "transparent",
                  border: "none",
                  color: "#94a3b8",
                  fontSize: "1.1rem",
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  transition: "color 0.15s, background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#0f172a";
                  e.currentTarget.style.background = "#f1f5f9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#94a3b8";
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <i className="fa-solid fa-xmark" />
              </button>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "4px",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "#fef3c7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#d97706",
                    fontSize: "1.1rem",
                    flexShrink: 0,
                  }}
                >
                  <i className="fa-solid fa-triangle-exclamation" />
                </div>
                <h3
                  style={{
                    margin: 0,
                    color: "#0f172a",
                    fontSize: "1.05rem",
                    fontWeight: 700,
                  }}
                >
                  Unsaved Changes
                </h3>
              </div>
              <p
                style={{
                  margin: "0 0 16px 0",
                  color: "#64748b",
                  fontSize: "0.85rem",
                  lineHeight: 1.5,
                }}
              >
                You have reorganized pages in student Answer Papers that
                haven&apos;t been saved. Do you want to save them before
                closing?
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <button
                  onClick={() => {
                    setShowCloseGuard(false);
                    setHistory([]);
                    onClose();
                  }}
                  style={{
                    padding: "8px 14px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: "#f8fafc",
                    color: "#475569",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "0.82rem",
                  }}
                >
                  Discard &amp; Close
                </button>
                <button
                  onClick={async () => {
                    await handleSaveChanges();
                    setShowCloseGuard(false);
                    onClose();
                  }}
                  disabled={isSaving}
                  style={{
                    padding: "8px 18px",
                    borderRadius: "6px",
                    border: "none",
                    background: "#16a34a",
                    color: "#ffffff",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: "0.82rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  {isSaving ? (
                    <i className="fa-solid fa-spinner fa-spin" />
                  ) : (
                    <i className="fa-solid fa-floppy-disk" />
                  )}
                  <span>Save &amp; Close</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SmartSplitDocumentViewer;
